// src/screens/StreamWatchScreen.js
// ============================================================================
// PERSONAL STREAM WATCH PAGE v2 (Streams directory)
//
// Twitch-style, browser-size aware:
//  - Wide (≥1000px): capped-size player + info/details in a left column,
//    live chat in a full-height right rail. The player is sized so the whole
//    16:9 frame fits the viewport (no more page-swallowing video).
//  - Narrow: stacked — player, info, details, then a fixed-height chat.
//
// Engagement: Like + Subscribe (toggleStreamLike/toggleStreamFollow —
// subscribers get an in-app "X is live" notification on go-live), and a
// public chat (streamChat/{ownerUid}/messages, same posture as event chat:
// logged-out read + sign-up CTA, signed-in post as themselves).
//
// Watch metering: signed-in viewers heartbeat the month-keyed streamAudience
// roster (rules-metered) → feeds the REDUCED-RATE personal-stream comp signal.
// ============================================================================

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Image,
  Platform,
  KeyboardAvoidingView,
  Modal,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  doc, getDoc, getDocFromServer, setDoc, updateDoc, increment, serverTimestamp,
  collection, addDoc, onSnapshot, query, orderBy, limit,
} from 'firebase/firestore';
import { db, auth } from '../../firebaseConfig';
import { Ionicons } from '@expo/vector-icons';
import LiveHlsPlayer from '../components/LiveHlsPlayer';
import Header from '../components/Header';
import { streamGameName, streamGameArtUrl } from '../constants/streamGames';
import { setPageMeta, resetPageMeta } from '../utils/pageMeta';
import { peekStreamCard } from '../utils/watchCardHandoff';
import StreamRumble from '../components/StreamRumble';
import { waveLabel } from '../utils/rumblePayment';
import SpendingLimitNotice from '../components/SpendingLimitNotice';
import useTipLimitNotices from '../hooks/useTipLimitNotices';
import {
  limitLines, cardLine, fitsLimit, savedCardStatus, cardStatusFromError, limitFromError,
  CONFIRMING_TEXT, STILL_CONFIRMING_TEXT,
} from '../utils/spendingLimit';
import JoinMembershipModal from '../components/JoinMembershipModal';
import useMembershipsLive from '../hooks/useMembershipsLive';
import {
  useMyChannelMembership, useChatGate, deliveredPerks, PERK_LABELS, LISTED_PERKS,
  membershipErrorMessage, membershipPrefsEpoch, formatMembershipDate,
  claimMemberBadge, setChannelChatMembersOnly,
  useMyMembership, teamDisplayName,
} from '../utils/membershipPayment';
import {
  badgeLabel, badgeColors, badgeA11y, isTierKey, tierNameOf, tierKeyOfRank, chatTierLine, chatMinTierAbove,
  memberTierBelowChat, lowestOnRank, videoTierChip, yourTierLine, SWITCH_TIER_LABEL, chatGateReachable,
  profileCanSwitch, chatChannelTiers, pendingSwitchReaches,
} from '../utils/membershipTiers';

const COLORS = {
  BG: '#0b0b0b',
  CARD: '#111',
  CARD_BORDER: '#222',
  GOLD: '#FCD000',
  TEXT: '#EDEDED',
  DIM: '#888',
  LIVE: '#e02222',
};

const CHAT_WIDTH = 340;
const CHAT_COLLAPSED_WIDTH = 36;
const RAIL_WIDTH = 220;
const RAIL_COLLAPSED_WIDTH = 56;
const WIDE_BREAKPOINT = 1000;
// Channel memberships (evergreen 3.5a): below this player width the engage
// row (report, like, Follow, Join/Member) moves to its own full-width line
// under the avatar and title, so its buttons never squeeze the title.
const ENGAGE_WRAP_MIN_PX = 560;
// A member whose access ends within this window may have just lapsed: a
// refused send then gets the neutral line and one server read of the pair doc.
const LAPSE_NEAR_MS = 10 * 60 * 1000;

// Collapse prefs persist across visits (web only — Twitch behavior).
const readPref = (key, fallback) => {
  try {
    if (typeof localStorage !== 'undefined') {
      const v = localStorage.getItem(key);
      if (v !== null) return v === '1';
    }
  } catch (_) { /* private mode etc. */ }
  return fallback;
};
const writePref = (key, value) => {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, value ? '1' : '0');
  } catch (_) { /* noop */ }
};

// Names + art resolve across BOTH catalogs (GAME_PROFILES + browse-only
// extras with gameImages/{ABBR}.webp art) — see constants/streamGames.js.
const gameName = (key) => (key ? streamGameName(key) : null);

// Display name for a stream's game — 'other' shows the streamer's own text.
const streamGameLabel = (s) => (s?.game === 'other' && s?.gameCustom ? s.gameCustom : gameName(s?.game));

const gameImageUrl = streamGameArtUrl;

const fmtCount = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n || 0));

const fmtDuration = (startedAtMs, nowMs) => {
  if (!startedAtMs) return '';
  const s = Math.max(0, Math.floor((nowMs - startedAtMs) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// YouTube-phrasing for the description box header ("Started streaming 3 hours ago").
const fmtStartedAgo = (startedAtMs, nowMs) => {
  if (!startedAtMs) return '';
  const mins = Math.max(1, Math.floor((nowMs - startedAtMs) / 60000));
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  return `${hours} hour${hours === 1 ? '' : 's'} ago`;
};

const openExternal = (url) => {
  const href = url.startsWith('http') ? url : `https://${url}`;
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.open(href, '_blank', 'noopener');
  }
};

// Split description text into plain/link segments so URLs render clickable
// (YouTube-style — streamers drop their socials/Discord in the details).
const URL_RE = /((?:https?:\/\/|www\.)[^\s]+)/g;
const renderLinkedText = (text) => {
  const parts = String(text || '').split(URL_RE);
  return parts.map((part, i) => {
    if (URL_RE.test(part)) {
      URL_RE.lastIndex = 0; // reset stateful global regex between tests
      return (
        <Text key={i} style={styles.descLink} onPress={() => openExternal(part)}>
          {part}
        </Text>
      );
    }
    URL_RE.lastIndex = 0;
    return <Text key={i}>{part}</Text>;
  });
};

// ---------------------------------------------------------------------------
// DESCRIPTION BOX — YouTube-style details area under the title: live stats
// line, expandable description with clickable links, game tag.
// ---------------------------------------------------------------------------
const DescriptionBox = ({ stream, nowMs, style }) => {
  const [expanded, setExpanded] = useState(false);
  const [artFailed, setArtFailed] = useState(false);
  const hasDetails = !!stream.details;
  const isLong = hasDetails && (stream.details.length > 160 || stream.details.includes('\n'));
  const artUrl = stream.game ? gameImageUrl(stream.game) : null;

  return (
    <View style={[styles.descBox, style]}>
      <Text style={styles.descStats}>
        {stream.live
          ? `${fmtCount(stream.viewers)} watching now${stream.startedAtMs ? `  ·  Started streaming ${fmtStartedAgo(stream.startedAtMs, nowMs)}` : ''}`
          : 'Offline'}
      </Text>

      {/* Game info sits ABOVE the description (founder 2026-07-09) */}
      {stream.game ? (
        <View style={styles.descGameRow}>
          {artUrl && !artFailed ? (
            <Image
              source={{ uri: artUrl }}
              style={styles.descGameArt}
              resizeMode="cover"
              onError={() => setArtFailed(true)}
            />
          ) : (
            <View style={styles.descGameTile}>
              <Ionicons name="game-controller-outline" size={18} color="#999" />
            </View>
          )}
          <View>
            <Text style={styles.descGameName}>{streamGameLabel(stream)}</Text>
            <Text style={styles.descGameSub}>Gaming</Text>
          </View>
        </View>
      ) : null}

      {hasDetails ? (
        <View style={styles.descTextBlock}>
          <Text style={styles.descText} numberOfLines={expanded || !isLong ? undefined : 3}>
            {renderLinkedText(stream.details)}
          </Text>
          {isLong ? (
            <Text style={styles.descToggle} onPress={() => setExpanded((v) => !v)}>
              {expanded ? 'Show less' : '...more'}
            </Text>
          ) : null}
        </View>
      ) : null}
    </View>
  );
};

// The live HLS player is components/LiveHlsPlayer.js — a double-buffered
// <video> pair that preloads src changes invisibly and only swaps once a
// frame has painted, with a controlled `muted` prop (the parent owns mute
// state). This screen and TeamStreamScreen both consume it.

// Custom live controls: play/pause · mute · LIVE (snap to edge) · fullscreen.
// Shown on mouse move / tap, auto-hidden after 3s (always shown while paused).
// NO native `controls` (founder 2026-07-09): a live stream is pause-and-live
// only — no seek bar, no elapsed/total time readout.
//
// Contract (LiveHlsPlayer era): `videoEl` is the ACTIVE element the double
// buffer is showing (handed over via onActiveVideoChange on every flip; null
// before the first load). `muted` is DISPLAY-ONLY — the parent owns mute
// state and LiveHlsPlayer applies it to the element; writing element.muted
// here would fight the controlled prop. Pause/seek/play are NOT managed by
// the player, so togglePlay/jumpToLive still drive the element directly.
// Exported for TeamStreamScreen (Team Streams v1) — the focused squad tile
// carries the same pause/mute/LIVE/fullscreen controls.
//
// `showStarting` (10-15, this screen only): until the active element first
// plays, a "Starting stream" chip replaces the round play button. TeamStream
// does not pass it and keeps the old behavior exactly.

// Cap on the starting chip: a feed with no first frame by then falls back to
// the plain behavior (play button only if paused), so the chip never claims
// a start forever on a broken feed. Restarts when playback is retried.
const START_HOLD_MS = 20000;
const STARTING_LABEL = 'Starting stream';

// Spinner chip centered over the player. label null = spinner only (the
// loading shell, for a channel its card showed as offline).
const StartingIndicator = ({ label = STARTING_LABEL }) => (
  <View style={styles.startingWrap} pointerEvents="none">
    <View style={styles.startingChip}>
      <ActivityIndicator size="small" color="#fff" />
      {label ? <Text style={styles.startingChipText}>{label}</Text> : null}
    </View>
  </View>
);

export const LivePlayerOverlay = ({ videoEl, containerRef, muted, onMuteToggle, showStarting = false }) => {
  const [visible, setVisible] = useState(true);
  const [paused, setPaused] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const hideTimer = useRef(null);
  // Starting chip state (showStarting only). Before the first frame a paused
  // element usually means "still loading": the play button waits for a real
  // pause (a pause after play, or a refused autoplay) or the hold cap.
  const [started, setStarted] = useState(false);
  const [pausedForReal, setPausedForReal] = useState(false);
  const [startTimedOut, setStartTimedOut] = useState(false);

  const poke = useCallback(() => {
    setVisible(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setVisible(false), 3000);
  }, []);

  // Mirror the ACTIVE element's play/pause state. Keyed on [videoEl]: the
  // double buffer swaps elements on src changes, and a bind-once-at-mount
  // pattern would leave listeners on a dead (or null) element — the element
  // arrives asynchronously, after this overlay first mounts.
  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    poke(); // element handover is a user-visible moment; surface the controls
    const v = videoEl;
    if (!v) { setPaused(false); return undefined; }
    const onPlay = () => setPaused(false);
    const onPause = () => setPaused(true);
    setPaused(!!v.paused); // seed: a flipped-in element is usually already playing
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
    };
  }, [videoEl, poke]);

  // Starting chip: track the ACTIVE element's first frame. Re-seeded on every
  // handover; a flipped-in element arrives with a frame and already playing.
  useEffect(() => {
    if (Platform.OS !== 'web' || !showStarting) return undefined;
    const v = videoEl;
    const playingNow = !!v && !v.paused && v.readyState >= 3;
    setStarted(playingNow);
    // Metadata in but still paused: the player's play() was refused.
    setPausedForReal(!!v && !!v.paused && v.readyState >= 1);
    setStartTimedOut(false);
    if (playingNow) return undefined;
    let bound = true;
    let didStart = false;
    let holdTimer = null;
    const armHold = () => {
      clearTimeout(holdTimer);
      holdTimer = setTimeout(() => { if (bound) setStartTimedOut(true); }, START_HOLD_MS);
    };
    armHold();
    if (!v) return () => { bound = false; clearTimeout(holdTimer); };
    const onPlaying = () => {
      didStart = true;
      clearTimeout(holdTimer);
      setStarted(true);
      setPausedForReal(false);
    };
    const onPlay = () => {
      setPausedForReal(false);
      if (!didStart) { setStartTimedOut(false); armHold(); }
    };
    const onPause = () => setPausedForReal(true);
    // LiveHlsPlayer calls play() from its own loadedmetadata listener, bound
    // before this one. Read paused a microtask later, so its muted retry
    // after a refused unmuted play() lands first.
    const onMeta = () => {
      Promise.resolve().then(() => { if (bound && v.paused) setPausedForReal(true); });
    };
    v.addEventListener('playing', onPlaying);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('loadedmetadata', onMeta);
    return () => {
      bound = false;
      clearTimeout(holdTimer);
      v.removeEventListener('playing', onPlaying);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('loadedmetadata', onMeta);
    };
  }, [videoEl, showStarting]);

  // Container activity + fullscreen listeners bind once — the container DOM
  // node (RNW View → div, more reliable than RNW mouse props) and document
  // are stable for this overlay's lifetime.
  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    const el = containerRef.current;
    // Per-container: the squad mosaic mounts one overlay per tile, and only
    // the tile that actually went full screen should show "contract".
    const onFsChange = () => setIsFullscreen(!!(typeof document !== 'undefined' && document.fullscreenElement && document.fullscreenElement === el));
    if (el && el.addEventListener) {
      el.addEventListener('mousemove', poke);
      el.addEventListener('touchstart', poke);
    }
    if (typeof document !== 'undefined') document.addEventListener('fullscreenchange', onFsChange);
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (el && el.removeEventListener) {
        el.removeEventListener('mousemove', poke);
        el.removeEventListener('touchstart', poke);
      }
      if (typeof document !== 'undefined') document.removeEventListener('fullscreenchange', onFsChange);
    };
  }, [containerRef, poke]);

  const togglePlay = () => {
    if (!videoEl) return;
    if (videoEl.paused) jumpToLive();
    else videoEl.pause?.();
    poke();
  };

  // Resuming a live stream always snaps back to the live edge — there is no
  // "behind live" position a viewer can hold (pause and live, that's it).
  const jumpToLive = () => {
    const v = videoEl;
    if (!v) return;
    try {
      if (v.seekable && v.seekable.length) {
        v.currentTime = Math.max(0, v.seekable.end(v.seekable.length - 1) - 3);
      }
    } catch (_) { /* pre-metadata — play() alone is fine */ }
    v.play?.().catch(() => {});
    poke();
  };

  const toggleMute = () => {
    // State-only: the parent flips its muted state and LiveHlsPlayer applies
    // it to whichever element is active — poking the element here would race
    // the double buffer's flip.
    onMuteToggle?.();
    poke();
  };

  const toggleFullscreen = () => {
    if (typeof document === 'undefined') return;
    const el = containerRef.current;
    if (!document.fullscreenElement) el?.requestFullscreen?.().catch(() => {});
    else document.exitFullscreen?.();
    poke();
  };

  if (Platform.OS !== 'web') return null;
  // No element yet counts as starting: the player hands one over right
  // after it mounts, and the chip must not blink off in between.
  const starting = showStarting && !startTimedOut
    && (!videoEl || (!started && !(paused && pausedForReal)));
  // While starting, the bar is not pinned open with a play icon.
  const shown = visible || (paused && !starting);

  return (
    <>
      {starting ? (
        <StartingIndicator />
      ) : paused ? (
        <Pressable style={styles.centerPlayBtn} onPress={togglePlay}>
          <Ionicons name="play" size={30} color="#fff" />
        </Pressable>
      ) : null}
      {shown ? (
        <View style={styles.playerControlsBar} pointerEvents="box-none">
          <TouchableOpacity style={styles.playerCtrlBtn} onPress={togglePlay}>
            <Ionicons name={paused && !starting ? 'play' : 'pause'} size={16} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.playerCtrlBtn} onPress={toggleMute}>
            <Ionicons name={muted ? 'volume-mute' : 'volume-high'} size={16} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.playerLiveChip} onPress={jumpToLive}>
            <View style={styles.playerLiveDot} />
            <Text style={styles.playerLiveText}>LIVE</Text>
          </TouchableOpacity>
          <View style={{ flex: 1 }} />
          <TouchableOpacity style={styles.playerCtrlBtn} onPress={toggleFullscreen}>
            <Ionicons name={isFullscreen ? 'contract' : 'expand'} size={16} color="#fff" />
          </TouchableOpacity>
        </View>
      ) : null}
    </>
  );
};

// ---------------------------------------------------------------------------
// LIVE CHANNELS RAIL (wide screens) — Twitch-style left sidebar. Collapsed =
// avatars only; expanded = avatar + name/game/viewers. Click hops streams.
// ---------------------------------------------------------------------------
const LiveRail = ({ collapsed, onToggle, currentUserId, navigation }) => {
  const [channels, setChannels] = useState([]);
  const functions = getFunctions();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const fn = httpsCallable(functions, 'listLiveStreams');
        const result = await fn();
        if (!cancelled) setChannels(result.data?.streams || []);
      } catch (_) { /* rail is best-effort */ }
    };
    load();
    const poll = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(poll); };
  }, [functions]);

  return (
    <View style={[styles.rail, { width: collapsed ? RAIL_COLLAPSED_WIDTH : RAIL_WIDTH }]}>
      <View style={[styles.railHeader, collapsed && { justifyContent: 'center' }]}>
        {!collapsed && <Text style={styles.railTitle}>LIVE CHANNELS</Text>}
        <TouchableOpacity
          onPress={onToggle}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityLabel={collapsed ? 'Expand live channels' : 'Collapse live channels'}
        >
          <Ionicons
            name={collapsed ? 'chevron-forward' : 'chevron-back'}
            size={16}
            color="#888"
          />
        </TouchableOpacity>
      </View>

      <ScrollView showsVerticalScrollIndicator={false}>
        {channels.map((s) => {
          const isCurrent = s.userId === currentUserId;
          return (
            <TouchableOpacity
              key={s.userId}
              style={[styles.railRow, collapsed && styles.railRowCollapsed, isCurrent && styles.railRowActive]}
              onPress={() => { if (!isCurrent) navigation.navigate('StreamWatch', { channel: s.userName || s.userId }); }}
            >
              {s.avatarUrl ? (
                <Image source={{ uri: s.avatarUrl }} style={styles.railAvatar} />
              ) : (
                <View style={[styles.railAvatar, styles.railAvatarFallback]}>
                  <Text style={styles.railAvatarText}>
                    {(s.displayName || '??').slice(0, 2).toUpperCase()}
                  </Text>
                </View>
              )}
              {!collapsed && (
                <>
                  <View style={{ flex: 1, minWidth: 0, marginLeft: 8 }}>
                    <Text style={styles.railName} numberOfLines={1}>{s.displayName}</Text>
                    <Text style={styles.railGame} numberOfLines={1}>{streamGameLabel(s) || 'Streaming'}</Text>
                  </View>
                  <View style={styles.railViewerCell}>
                    <View style={styles.railLiveDot} />
                    <Text style={styles.railViewers}>{fmtCount(s.viewers)}</Text>
                  </View>
                </>
              )}
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );
};

// ---------------------------------------------------------------------------
// STREAM CHAT — scoped per chat SESSION: streamChat/{ownerUid}_{epochMs}
// /messages. The epoch is a grace-windowed id minted server-side
// (streamRelayBridge touchChatSession, 2026-07-23): it survives OBS
// restarts, network drops, and page refreshes, and only rotates (old room
// swept) when the streamer has been offline for over an hour — so "new
// stream = clean chat" still holds across real gaps. sessionId null =
// stream offline → chat is closed.
// ---------------------------------------------------------------------------
// Amber/yellow Super Crit tiers need dark text; the rest carry white.
const DARK_TEXT_TIER_COLORS = new Set(['#FFB300']);
const superCritTextColor = (bg) => (DARK_TEXT_TIER_COLORS.has(String(bg || '').toUpperCase()) ? '#0b0b0b' : '#fff');
const fmtDollars = (centsAmt) => {
  const d = (Number(centsAmt) || 0) / 100;
  return `$${d % 1 ? d.toFixed(2) : d.toFixed(0)}`;
};

// ---------------------------------------------------------------------------
// Channel memberships in chat (evergreen 3.5a). Money-free: chat only ever
// shows who is a member (a badge the member can switch off) and the room's
// members-only mode. The rules decide who may post; the client only explains.
// ---------------------------------------------------------------------------
const CHAT_MEMBERS_ONLY = 'Chat is members-only right now.';
const CHAT_MEMBERS_ONLY_JOIN = 'Chat is members-only right now. Join to chat.';
const CHAT_CANT_SEND = "You can't send messages in this chat right now.";
const CHAT_MUTED = "You can't send messages in this chat. The streamer has muted you.";
const CHAT_PAUSED_HINT = 'Members-only chat is paused while memberships are off. Anyone can chat right now.';
// Team memberships (evergreen 3.5b): the squad room's twins. Money-free; the
// roster is admitted by the rule, so the squad page never tells them to join.
const CHAT_TEAM_MEMBERS_ONLY = 'Squad chat is members-only right now.';
const CHAT_TEAM_MEMBERS_ONLY_JOIN = 'Squad chat is members-only right now. Join to chat.';
const CHAT_TEAM_PAUSED_HINT = 'Members-only squad chat is paused while team memberships are off. Anyone can chat right now.';
// The owner's turn-on confirm and the on-state line, by room kind (the team
// lines are the captain screen's chatOnBody / chatOnClosed).
const CHAT_GATE_COPY = {
  channel: {
    chipLabel: 'Members-only',
    chipLabelPaused: 'Members-only (paused)',
    chipLabelOff: 'Members-only (off)',
    sheetTitle: 'Members-only chat',
    sheetTitlePaused: 'Members-only (paused)',
    onBody: 'Only members can type. Super Crits, Rumbles and lines from your linked chats still show.',
    onClosed: "New viewers can't join right now, so only current members will be able to type.",
    onNow: 'Only members can type right now.',
    pausedHint: CHAT_PAUSED_HINT,
    banner: CHAT_MEMBERS_ONLY,
    bannerJoin: CHAT_MEMBERS_ONLY_JOIN,
  },
  team: {
    chipLabel: 'Members-only squad chat',
    chipLabelPaused: 'Members-only squad chat (paused)',
    chipLabelOff: 'Members-only squad chat (off)',
    sheetTitle: 'Members-only squad chat',
    sheetTitlePaused: 'Members-only squad chat (paused)',
    onBody: 'Only members and your squad can type. Super Crits, Rumbles and lines from linked chats still show.',
    onClosed: "New viewers can't join right now, so only current members and the squad will be able to type.",
    onNow: 'Only members and the squad can type right now.',
    pausedHint: CHAT_TEAM_PAUSED_HINT,
    banner: CHAT_TEAM_MEMBERS_ONLY,
    bannerJoin: CHAT_TEAM_MEMBERS_ONLY_JOIN,
  },
};
// An unknown kind reads as a channel room (the prop's default).
const chatGateCopyOf = (gateKind) => (gateKind === 'team' ? CHAT_GATE_COPY.team : CHAT_GATE_COPY.channel);
const PAIR_CHANGE_RECENT_MS = 5000;
const HOUR_MS = 60 * 60 * 1000;
// A failed badge claim waits this long before the next send may try again.
const BADGE_CLAIM_RETRY_MS = 5 * 60 * 1000;

// A chatGates doc only switches THIS chat when its kind is this room's kind (a
// missing kind = channel: a team gate never touches a channel room, and a
// channel gate never touches a squad room), like the rule.
const chatGateOn = (gate, gateKind = 'channel') => gate?.membersOnly === true
  && (gate.kind ?? 'channel') === gateKind;

// Tiers (R5, 5.11 F6): a member whose tier is below the chat's minimum (and
// who carries no members-only chat perk from a recent change) is refused by
// the rule's memberTierOk; the gate's minimum is named when it is above the
// lowest tier the channel offers, else today's members-only strings stay.
const chatTierLow = (gate, memberInfo) => !!memberInfo
  && memberTierBelowChat(gate?.minTierRank, memberInfo.tierRank, memberInfo.chatPerk);

// What a refused send (permission-denied) tells the sender. The rule ANDs the
// mute check with the members-only gate and mutes are unreadable to clients,
// so only the members-only case can be named; `recheck` asks for one server
// read of the pair doc (a member who may have just lapsed or been removed).
// On a squad room the roster arrives as viewerIsMember true (the rule admits
// them), so their refusal reads as a mute, never as members-only.
// lowestTierRank: the lowest tier the channel offers (0 = unknown).
// gateReachable: a tier the channel sells can post (chatGateReachable); when
// none can (the perk off: CHAT_GATE_NO_TIER_RANK, or every tier from the
// minimum up off), no line says "Join to chat." or "Switch tiers to chat."
// The caller also passes false for a member whose pending switch already
// reaches the minimum (R5REV5-1).
// gateKind 'team' (3.5b): a squad room's non-member gets the team banner;
// the tier lines are channel only.
const chatSendRefusal = ({
  gate, membershipsLive, viewerIsMember, isOwner, memberInfo, pairChangedAtMs, canJoin, nowMs, lowestTierRank = 0,
  gateReachable = true, gateKind = 'channel',
}) => {
  const gateOn = chatGateOn(gate, gateKind);
  if (gateOn && membershipsLive === true && viewerIsMember !== true && !isOwner) {
    if (gateKind === 'team') {
      const copy = chatGateCopyOf(gateKind);
      return { text: canJoin ? copy.bannerJoin : copy.banner, recheck: false };
    }
    const join = canJoin && gateReachable;
    const minTier = chatMinTierAbove(gate.minTierRank, lowestTierRank);
    if (minTier) return { text: chatTierLine(minTier, join ? 'join' : 'plain'), recheck: false };
    return { text: join ? CHAT_MEMBERS_ONLY_JOIN : CHAT_MEMBERS_ONLY, recheck: false };
  }
  // A mode kept through a pause, or the stale window after a flag change:
  // never a false members-only claim, never a mute accusation.
  if (gateOn && membershipsLive !== true) return { text: CHAT_CANT_SEND, recheck: false };
  if (gateOn && !isOwner && viewerIsMember === true && chatTierLow(gate, memberInfo)
    && nowMs - (Number(pairChangedAtMs) || 0) >= PAIR_CHANGE_RECENT_MS) {
    // A minimum that is not a tier (the perk is off): no tier to name or switch to.
    return { text: chatTierLine(tierKeyOfRank(gate.minTierRank), gateReachable ? 'switch' : 'plain') || CHAT_CANT_SEND, recheck: false };
  }
  if (gateOn && !isOwner && viewerIsMember === true && memberInfo && (
    memberInfo.activeUntilMs - nowMs < LAPSE_NEAR_MS
    || nowMs - (Number(pairChangedAtMs) || 0) < PAIR_CHANGE_RECENT_MS
  )) {
    return { text: CHAT_CANT_SEND, recheck: true };
  }
  return { text: CHAT_MUTED, recheck: false };
};

// Member badge claims (5.7). The server writes the public badge; the member's
// client only asks, on its first successful send of a session and again when
// its own entry is missing or ends within the hour and the server's last
// nextClaimAtMs has passed. Module-level so collapsing chat or crossing the
// breakpoint (a StreamChat remount) never re-arms a claim. One in flight per
// session and creator; the gate re-opens early when the member's pair doc
// changes (status, access end, removal, badge perk) or when the member saves
// their membership prefs in this app session. The target is {kind, id}: a
// channel ('channel', creator uid) or, on the squad page (3.5b), a team
// ('team', team id); the one claimMemberBadge callable takes both.
const badgeClaims = new Map(); // `${sid}:${kind}:${id}` -> { sent, gateMs, inFlight, epoch, prefsEpoch }
const memberEpochOf = (info) => (info
  ? `${info.status}|${info.activeUntilMs}|${info.removedByCreator === true}|${info.badgePerk === true}|${info.tierKey || ''}`
  : '');
const maybeClaimMemberBadge = ({ sid, target, memberInfo, ownEntry, prefsEpoch, nowMs, call }) => {
  if (!sid || !target || !target.kind || !target.id || !memberInfo) return false;
  const live = memberInfo.status !== 'ended'
    && memberInfo.activeUntilMs > nowMs
    && memberInfo.removedByCreator !== true;
  if (!live) return false;
  const key = `${sid}:${target.kind}:${target.id}`;
  const epoch = memberEpochOf(memberInfo);
  let st = badgeClaims.get(key);
  if (!st) {
    st = { sent: false, gateMs: 0, inFlight: false, epoch, prefsEpoch };
    badgeClaims.set(key, st);
  }
  if (st.epoch !== epoch || st.prefsEpoch !== prefsEpoch) {
    st.gateMs = 0;
    st.epoch = epoch;
    st.prefsEpoch = prefsEpoch;
  }
  if (st.inFlight) return false;
  const until = Number(ownEntry?.until) || 0;
  const expiring = until - nowMs <= HOUR_MS;
  if (st.sent && !(expiring && nowMs >= st.gateMs)) return false;
  st.sent = true;
  st.inFlight = true;
  Promise.resolve()
    .then(() => call(target))
    .then((res) => {
      const next = Number(res?.nextClaimAtMs);
      st.gateMs = Number.isFinite(next) ? next : Date.now() + HOUR_MS;
    })
    .catch((err) => {
      st.gateMs = Date.now() + BADGE_CLAIM_RETRY_MS;
      console.warn('[StreamChat] member badge claim failed:', err?.message);
    })
    .finally(() => { st.inFlight = false; });
  return true;
};

// ms -> "September 2026" (the Member panel's "Member since"); '' when absent.
const fmtMonthYear = (ms) => (Number(ms) > 0
  ? new Date(Number(ms)).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  : '');
const memberTenureLabel = (m) => {
  const n = Number(m) || 0;
  return n <= 1 ? 'New member' : `Member for ${n} months`;
};

// Exported for TeamStreamScreen (Team Streams v1): the squad page runs this
// same chat on the squad's own session. Additive props (defaults preserve
// personal-stream behavior byte-for-byte):
//  - title: header label ("Squad Chat" on the squad page)
//  - hideActions: suppresses the whole Rumble/Super Crit row
//  - hideSuperCrit: hides ONLY the Super Crit button. Squad members can't
//    Super Crit their own squad (server-enforced), so the squad page hides
//    the lane for them. The watch page passes it when getStreamPlayback says
//    the channel is not open for Super Crits (tipsEnabled === false: the
//    streamer has not passed the ID + age check; the server refuses the
//    purchase too).
//  - rumblesEnabled: shows the Rumble button (evergreen 3.3). Only the
//    server's rumblesEnabled from getStreamPlayback / getTeamStream opens it
//    (flag + live + a verified, non-admin recipient + not your own channel;
//    sanction/hold checks are send-time only); the squad
//    page also passes false to its own members. Default false, so a consumer
//    that never passes it (the popout) shows no Rumble door.
//  - onPopout: renders an open-in-new-window button in the header (watch page
//    passes it on web — chat-only popout for OBS docks / second monitors).
// Channel memberships (evergreen 3.5a; all default to today's behavior; from
// 3.5b the squad page passes them too, for its team, with gateKind 'team'):
//  - gateKey: the channel uid (a squad room: the team id) whose chatGates doc
//    this room follows.
//  - membershipsLive: getStreamPlayback's raw field (the rule's flag/pilot
//    gate); null or missing = unknown, never shown as paused.
//  - viewerIsMember: false = not a member; null = unknown (the membership
//    hook is loading or failed), when the rule alone decides.
//  - memberInfo: {status, activeUntilMs, updatedAtMs, removedByCreator,
//    badgePerk} from the viewer's own pair doc (badge claims, lapse check).
//  - onJoin: set only when the page would show Join; the members-only banner
//    offers Join only then.
//  - badgeCreatorUid: the channel a member's badge is claimed for.
//  - canToggleMembersOnly / onToggleMembersOnly(on) -> Promise: the owner's
//    "Members-only" chip in the header (the chip also shows for the owner
//    while the gate is on, and read-only in the popout, hideActions, for an
//    admin while the gate is on). Without onToggleMembersOnly, or for anyone
//    but the streamer, the chip only shows the state.
//  - newJoinsClosed: new viewers can't join right now (the creator paused
//    new members, or memberships are dark and this is a pilot); adds a line
//    to the turn-on confirm.
//  - lowestTierRank (tiers, R5): the lowest tier the channel offers (from
//    getStreamPlayback's membershipTiers; 0 = unknown): the members-only
//    lines name the chat's minimum tier only when it is above it (F6).
//  - onSwitchTier: set for a member whose tier is below the chat's
//    minimum; the banner then offers "Switch tier".
//  - channelTiers (tiers): getStreamPlayback's membershipTiers (null =
//    unknown). Join and "Switch tier" show only when a tier it lists can
//    post at the chat's minimum (chatGateReachable).
//  memberInfo also carries tierKey, tierRank and chatPerk (the pair doc's
//  tier and its members-only chat perk), the rule's memberTierOk inputs.
// Team memberships (evergreen 3.5b; defaults keep the watch page and the
// popout exactly as they were, and only the squad page passes them):
//  - gateKind: 'channel' (default) or 'team': which chatGates kind switches
//    this room, which pair collection a refused send re-reads, and the room's
//    members-only copy ("squad chat"). The tier lines and the tier banner
//    are channel only.
//  - badgeTarget: {kind, id}, the membership a member's badge is claimed for
//    (the squad page passes {kind: 'team', id: teamId}); without it,
//    badgeCreatorUid still means the channel.
//  - joinLabel: the members-only banner's Join accessibility label (the squad
//    page passes "Join Team X"); absent, the button keeps its own text.
// onUserPress(msg, memberBadge) also hands over the author's live member
// badge ({until, m, tk}, or t: true for a team badge) or null, for the user
// card's tenure and tier.
export const StreamChat = ({
  sessionId, senderName, streamerUid, navigation, style, onCollapse,
  onRumble, onSuperCrit, onUserPress, hiddenUids,
  title = 'Stream Chat', hideActions = false, hideSuperCrit = false,
  rumblesEnabled = false, onPopout,
  gateKey = null, membershipsLive = null, viewerIsMember = false, memberInfo = null,
  onJoin = null, badgeCreatorUid = null, canToggleMembersOnly = false,
  onToggleMembersOnly = null, newJoinsClosed = false,
  lowestTierRank = 0, onSwitchTier = null, channelTiers = null,
  gateKind = 'channel', badgeTarget = null, joinLabel = null,
}) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [pinned, setPinned] = useState(null);
  const [pinBusyId, setPinBusyId] = useState(null);
  const [unpinBusy, setUnpinBusy] = useState(false);
  const [chatError, setChatError] = useState(null);
  // Channel memberships (3.5a): public member badges on the session parent,
  // the room's members-only gate, and the owner's chip confirm.
  const [memberBadges, setMemberBadges] = useState(null);
  const { gate } = useChatGate(gateKey);
  const gateOn = chatGateOn(gate, gateKind);
  const gateCopy = chatGateCopyOf(gateKind);
  const badgeTargetEff = badgeTarget || (badgeCreatorUid ? { kind: 'channel', id: badgeCreatorUid } : null);
  const effectiveMembersOnly = gateOn && membershipsLive === true;
  const [gateSheetOpen, setGateSheetOpen] = useState(false);
  const [gateBusy, setGateBusy] = useState(false);
  const pairChangedAtRef = useRef(0);
  const memberEpoch = memberEpochOf(memberInfo);
  const scrollRef = useRef(null);
  // Stick-to-bottom latch. The subscription caps at 100 docs, so once the room
  // is busy messages.length stops changing — following must key off message
  // identity and content size, never off length. True = follow new messages;
  // flips false only when the viewer deliberately scrolls up.
  const pinnedToBottomRef = useRef(true);
  const lastMsgIdRef = useRef(null);
  const [hasUnseen, setHasUnseen] = useState(false);
  const isLoggedIn = !!auth.currentUser;
  // The streamer moderates their own chat: distinct name treatment + pin.
  const viewerIsStreamer = !!streamerUid && auth.currentUser?.uid === streamerUid;
  const functions = getFunctions();

  useEffect(() => {
    setMessages([]);
    // New session = fresh room: follow from the start.
    pinnedToBottomRef.current = true;
    lastMsgIdRef.current = null;
    setHasUnseen(false);
    if (!sessionId) return;
    const unsub = onSnapshot(
      query(
        collection(db, 'streamChat', sessionId, 'messages'),
        orderBy('timestamp', 'desc'),
        limit(100)
      ),
      (snap) => {
        setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })).reverse());
      },
      (err) => console.warn('[StreamChat] subscribe failed:', err.message)
    );
    return () => unsub();
  }, [sessionId]);

  // Pinned message lives on the session parent doc (server-written via the
  // pin callable) so every viewer's banner tracks the same snapshot. The same
  // parent carries the public member badges (3.5a, server-written by
  // claimMemberBadge), so they cost no extra listener.
  useEffect(() => {
    setPinned(null);
    setMemberBadges(null);
    if (!sessionId) return;
    const unsub = onSnapshot(
      doc(db, 'streamChat', sessionId),
      (snap) => {
        const d = snap.exists() ? snap.data() : null;
        setPinned(d ? (d.pinned || null) : null);
        setMemberBadges(d && d.memberBadges && typeof d.memberBadges === 'object' ? d.memberBadges : null);
      },
      () => {}
    );
    return () => unsub();
  }, [sessionId]);

  // When the viewer's own pair doc last changed (a removal or a lapse may
  // have just landed): a refused send within a few seconds gets the neutral
  // line and a server read instead of a mute accusation.
  useEffect(() => {
    if (memberEpoch) pairChangedAtRef.current = Date.now();
  }, [memberEpoch]);

  // Follow the newest message only while pinned to the bottom. A viewer who
  // scrolled up keeps their place and gets a "New messages" chip instead of
  // being yanked back down.
  useEffect(() => {
    const lastId = messages.length ? messages[messages.length - 1].id : null;
    if (!lastId || lastId === lastMsgIdRef.current) return;
    lastMsgIdRef.current = lastId;
    if (pinnedToBottomRef.current) {
      // Non-animated: under rapid appends an animated scroll can be cut short
      // mid-flight and settle above the bottom.
      setTimeout(() => scrollRef.current?.scrollToEnd({ animated: false }), 50);
    } else {
      setHasUnseen(true);
    }
  }, [messages]);

  const handleChatScroll = (e) => {
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
    // Slop absorbs wrapped rows and sub-pixel rounding — only a real scroll-up
    // counts as reading history.
    const atBottom = contentSize.height - contentOffset.y - layoutMeasurement.height <= 48;
    pinnedToBottomRef.current = atBottom;
    if (atBottom) setHasUnseen(false);
  };

  // Late layout growth (text wrap, avatars/images finishing) re-sticks a
  // pinned view; this also covers appends whose row heights offset exactly.
  const handleChatContentSize = () => {
    if (pinnedToBottomRef.current) scrollRef.current?.scrollToEnd({ animated: false });
  };

  const jumpToLatest = () => {
    pinnedToBottomRef.current = true;
    setHasUnseen(false);
    scrollRef.current?.scrollToEnd({ animated: true });
  };

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text || !sessionId) return;
    const u = auth.currentUser;
    if (!u) { navigation.navigate('SignUp'); return; }
    try {
      await addDoc(collection(db, 'streamChat', sessionId, 'messages'), {
        // Public handle (users.userName), never the auth display name — that's
        // the person's real name and chat is a public surface.
        user: senderName || 'Player',
        userId: u.uid,
        message: text.slice(0, 500),
        timestamp: serverTimestamp(),
      });
      setInputText('');
      // A member's first send of a session (and, rarely, later ones) asks the
      // server for the member badge (3.5a; a team badge on the squad page,
      // 3.5b). Fire-and-forget; never blocks chat.
      if (badgeTargetEff && memberInfo && !viewerIsStreamer) {
        maybeClaimMemberBadge({
          sid: sessionId,
          target: badgeTargetEff,
          memberInfo,
          ownEntry: memberBadges ? memberBadges[u.uid] : null,
          prefsEpoch: membershipPrefsEpoch(),
          nowMs: Date.now(),
          call: (t) => claimMemberBadge({ kind: t.kind, targetId: t.id }),
        });
      }
    } catch (err) {
      // Refused by the rules → permission-denied: a mute (the rules' exists()
      // check) or, from 3.5a, a members-only room this sender can't post in.
      const raw = String(err?.code || err?.message || '');
      if (raw.includes('permission-denied') || raw.includes('PERMISSION_DENIED')) {
        const refusal = chatSendRefusal({
          gate,
          membershipsLive,
          viewerIsMember,
          isOwner: viewerIsStreamer,
          memberInfo,
          pairChangedAtMs: Math.max(pairChangedAtRef.current, Number(memberInfo?.updatedAtMs) || 0),
          canJoin: !!onJoin,
          nowMs: Date.now(),
          lowestTierRank,
          // The banner's rule (R5REV5-1): a member whose pending switch already
          // reaches the minimum is never told to switch.
          gateReachable: !(viewerIsMember === true && pendingSwitchReaches(memberInfo, tierKeyOfRank(gate?.minTierRank)))
            && chatGateReachable(gate?.minTierRank, channelTiers),
          gateKind,
        });
        flashChatError(refusal.text);
        // One server read, so the membership listener redraws Join or Member
        // (the room's own pair collection: teamMemberships on a squad room).
        if (refusal.recheck && gateKey) {
          getDocFromServer(doc(
            db, gateKind === 'team' ? 'teamMemberships' : 'channelMemberships', `${gateKey}_${u.uid}`,
          )).catch(() => {});
        }
      }
      console.error('[StreamChat] send failed:', err);
    }
  };

  // Owner's members-only switch (3.5a). ON is confirmed first; OFF is always
  // allowed. The gate listener redraws the chip; a refusal shows the server's
  // sentence and closes the sheet.
  const setMembersOnly = async (on) => {
    if (gateBusy || !onToggleMembersOnly) return;
    setGateBusy(true);
    try {
      await onToggleMembersOnly(on);
      setGateSheetOpen(false);
    } catch (err) {
      setGateSheetOpen(false);
      flashChatError(membershipErrorMessage(err));
      console.warn('[StreamChat] members-only toggle failed:', err?.message);
    } finally {
      setGateBusy(false);
    }
  };

  const flashChatError = (text) => {
    setChatError(text);
    setTimeout(() => setChatError(null), 4000);
  };

  const pinMessage = async (messageId) => {
    if (pinBusyId) return;
    setPinBusyId(messageId);
    try {
      await httpsCallable(functions, 'pinStreamChatMessage')({ sessionId, messageId });
    } catch (err) {
      const raw = String(err?.message || '');
      flashChatError(raw.includes('already-pinned')
        ? 'A message is already pinned — unpin it first.'
        : 'Could not pin that message.');
      console.warn('[StreamChat] pin failed:', err.message);
    } finally {
      setPinBusyId(null);
    }
  };

  const unpinMessage = async () => {
    if (unpinBusy) return;
    setUnpinBusy(true);
    try {
      await httpsCallable(functions, 'unpinStreamChatMessage')({ sessionId });
    } catch (err) {
      flashChatError('Could not unpin the message.');
      console.warn('[StreamChat] unpin failed:', err.message);
    } finally {
      setUnpinBusy(false);
    }
  };

  // Every message type is pinnable (founder 2026-07-10) — one pin at a time,
  // so the buttons only exist while nothing is pinned.
  const pinAffordance = (msg) => (
    viewerIsStreamer && !pinned ? (
      <TouchableOpacity
        style={[styles.pinMsgBtn, pinBusyId === msg.id && { opacity: 0.6 }]}
        onPress={() => pinMessage(msg.id)}
        disabled={!!pinBusyId}
        accessibilityLabel="Pin message"
      >
        <Ionicons name="pin" size={12} color={COLORS.GOLD} style={{ marginRight: 5 }} />
        <Text style={styles.pinMsgBtnText}>Pin Message</Text>
      </TouchableOpacity>
    ) : null
  );

  // Streamer's own messages read differently: cyan handle + a host badge.
  // Every handle is tappable → user card (profile / hide / mute / report).
  // External (bridged) messages get a [Twitch]/[Kick]/[YouTube] badge in the
  // platform's color — founder-approved exception to names-from-
  // public_profiles (2026-07-28): these are NOT CritCash users, userId is
  // null, so the tap guard already disables the user card for them.
  const EXT_PLATFORMS = {
    twitch: { label: 'Twitch', color: '#9146FF' },
    kick: { label: 'Kick', color: '#53FC18' },
    youtube: { label: 'YouTube', color: '#FF4444' },
  };
  const renderUserName = (msg) => {
    // Unknown platform still badges as generic External — a bridged message
    // must NEVER render indistinguishably from a CritCash member.
    const ext = msg.type === 'external'
      ? (EXT_PLATFORMS[msg.platform] || { label: 'External', color: '#9aa0a6' })
      : null;
    const isStreamerMsg = !!msg.userId && msg.userId === streamerUid;
    // Member badge (3.5a): server-written, hour-granular, rendered only while
    // its until is ahead. The streamer's own treatment wins. A team badge
    // (t: true, written in a squad room, 3.5b) reads TEAM instead of MEMBER.
    const badge = !isStreamerMsg && !ext && msg.userId && memberBadges
      ? memberBadges[msg.userId] : null;
    const memberBadge = badge && Number(badge.until) > Date.now() ? badge : null;
    const teamBadge = memberBadge ? memberBadge.t === true : false;
    // Tiers (R5): the chip names the member's tier (tk) in its brand colors;
    // a badge written before tiers reads MEMBER.
    const tk = memberBadge && isTierKey(memberBadge.tk) ? memberBadge.tk : null;
    const chipColors = badgeColors(tk);
    return (
      <TouchableOpacity
        onPress={() => onUserPress && msg.userId && onUserPress(msg, memberBadge)}
        disabled={!onUserPress || !msg.userId}
      >
        {isStreamerMsg ? (
          <View style={styles.streamerNameWrap}>
            <View style={styles.streamerBadge}>
              <Ionicons name="videocam" size={9} color="#0b0b0b" />
            </View>
            <Text style={styles.chatMsgUserStreamer}>{msg.user}</Text>
          </View>
        ) : ext ? (
          <View style={styles.streamerNameWrap}>
            <View style={[styles.extBadge, { borderColor: ext.color }]}>
              <Text style={[styles.extBadgeText, { color: ext.color }]}>{ext.label}</Text>
            </View>
            <Text style={styles.chatMsgUser}>{msg.user}</Text>
          </View>
        ) : memberBadge ? (
          <View style={styles.streamerNameWrap}>
            {teamBadge ? (
              <View style={styles.memberChip} accessibilityLabel="Team member">
                <Text style={styles.memberChipText}>TEAM</Text>
              </View>
            ) : (
              <View
                style={[styles.memberChip, { backgroundColor: chipColors.fill, borderColor: chipColors.border }]}
                accessibilityLabel={badgeA11y(tk, memberBadge.m)}
              >
                <Text style={[styles.memberChipText, { color: chipColors.text }]}>{badgeLabel(tk)}</Text>
              </View>
            )}
            <Text style={styles.chatMsgUser}>{msg.user}</Text>
          </View>
        ) : (
          <Text style={styles.chatMsgUser}>{msg.user}</Text>
        )}
      </TouchableOpacity>
    );
  };

  const renderMessage = (msg) => {
    // Crit-era lines ('rumble' / 'gift', pre-3.3) render without the Crit amount;
    // new Rumbles arrive as 'rumbleWave' below.
    if (msg.type === 'rumble') {
      return (
        <View key={msg.id} style={styles.chatMsgBlock}>
          <View style={[styles.chatRumble, { marginBottom: 0 }]}>
            <Ionicons name="flash" size={13} color={COLORS.GOLD} style={{ marginRight: 6, marginTop: 2 }} />
            <Text style={styles.chatRumbleText}>
              <Text style={styles.chatRumbleUser}>{msg.user}</Text>
              {' '}rumbled the stream: {msg.wave}!
            </Text>
          </View>
          {pinAffordance(msg)}
        </View>
      );
    }
    if (msg.type === 'gift') {
      return (
        <View key={msg.id} style={styles.chatMsgBlock}>
          <View style={[styles.chatRumble, { marginBottom: 0 }]}>
            <Ionicons name="gift" size={13} color={COLORS.GOLD} style={{ marginRight: 6, marginTop: 2 }} />
            <Text style={styles.chatRumbleText}>
              <Text style={styles.chatRumbleUser}>{msg.user}</Text>
              {' '}shared the hype with {msg.recipients} viewer{msg.recipients === 1 ? '' : 's'} in chat!
            </Text>
          </View>
          {pinAffordance(msg)}
        </View>
      );
    }
    if (msg.type === 'rumbleWave') {
      // Rumble (evergreen 3.3), server-written once the charge is recorded.
      // Money-free by design: the line carries only the wave tier, never an
      // amount. Squad Rumbles carry squad:true.
      const label = waveLabel(msg.wave);
      return (
        <View key={msg.id} style={styles.chatMsgBlock}>
          <View style={[styles.chatRumble, { marginBottom: 0 }]}>
            <Ionicons name="flash" size={13} color={COLORS.GOLD} style={{ marginRight: 6, marginTop: 2 }} />
            <Text style={styles.chatRumbleText}>
              <Text style={styles.chatRumbleUser}>{msg.user}</Text>
              {' '}sent a{' '}
              <Text style={styles.chatRumbleUser}>{label ? `${label} ` : ''}Rumble</Text>
              {msg.squad ? ' to the squad' : ''}!
            </Text>
          </View>
          {pinAffordance(msg)}
        </View>
      );
    }
    if (msg.type === 'subscribe') {
      return (
        <View key={msg.id} style={styles.chatMsgBlock}>
          <View style={[styles.chatRumble, { marginBottom: 0 }]}>
            <Ionicons name="notifications" size={13} color={COLORS.GOLD} style={{ marginRight: 6, marginTop: 2 }} />
            <Text style={styles.chatRumbleText}>
              <Text style={styles.chatRumbleUser}>{msg.user}</Text>
              {' '}followed the channel!
            </Text>
          </View>
          {pinAffordance(msg)}
        </View>
      );
    }
    if (msg.type === 'memberJoin') {
      // "Became a member" line (3.5a), server-written after the first payment
      // when the member opted in. Money-free: no price, no plan. A team
      // membership's line (team: true, in a squad room, 3.5b) says so.
      return (
        <View key={msg.id} style={styles.chatMsgBlock}>
          <View style={[styles.chatRumble, { marginBottom: 0 }]}>
            <Ionicons name="star" size={13} color={COLORS.GOLD} style={{ marginRight: 6, marginTop: 2 }} />
            <Text style={styles.chatRumbleText}>
              <Text style={styles.chatRumbleUser}>{msg.user}</Text>
              {' '}{msg.team === true ? 'became a team member!' : 'became a member!'}
            </Text>
          </View>
          {pinAffordance(msg)}
        </View>
      );
    }
    if (msg.type === 'superCrit') {
      const bg = msg.color || '#FFB300';
      const fg = superCritTextColor(bg);
      return (
        <View key={msg.id} style={styles.chatMsgBlock}>
          <View style={[styles.superCritCard, { marginBottom: 0 }]}>
            <View style={[styles.superCritHeader, { backgroundColor: bg }]}>
              <Ionicons name="star" size={12} color={fg} style={{ marginRight: 5 }} />
              <Text style={[styles.superCritUser, { color: fg }]} numberOfLines={1}>{msg.user}</Text>
              {/* Squad Super Crits (Team Streams v1) carry squad:true — the
                  full amount shows with a "to the squad" suffix. */}
              <Text style={[styles.superCritAmount, { color: fg }]}>{fmtDollars(msg.amountCents)}{msg.squad ? ' to the squad' : ''}</Text>
            </View>
            {msg.message ? (
              <Text style={styles.superCritBody}>{msg.message}</Text>
            ) : null}
          </View>
          {pinAffordance(msg)}
        </View>
      );
    }
    return (
      <View key={msg.id} style={styles.chatMsgBlock}>
        <View style={[styles.chatMsg, { marginBottom: 0 }]}>
          {renderUserName(msg)}
          <Text style={styles.chatMsgText}>{msg.message}</Text>
        </View>
        {pinAffordance(msg)}
      </View>
    );
  };

  // Owner's "Members-only" chip: shown when the page says the owner can use
  // the mode (canToggleMembersOnly) or while this room's gate is on, for the
  // streamer, or read-only in the owner-gated popout (hideActions; only the
  // owner and admins get there). The watch page never shows it to viewers.
  // The state is the EFFECTIVE one: "paused" only on an explicit
  // membershipsLive === false (a missing field reads as plain on).
  const showGateChip = !!gateKey
    && (canToggleMembersOnly || (gateOn && (viewerIsStreamer || hideActions)));
  const gateChipState = !gateOn ? 'off' : membershipsLive === false ? 'paused' : 'on';
  const gateChipInteractive = showGateChip && viewerIsStreamer && !!onToggleMembersOnly;
  // Members-only room for a known non-member (never while membership is
  // unknown: the rule decides then). Join only when the page would show it.
  const showMembersOnlyBanner = !!sessionId && effectiveMembersOnly
    && !viewerIsStreamer && viewerIsMember === false;
  // Tiers (F6): the same banner for a member whose tier is below the chat's
  // minimum, with "Switch tier" when the page can open it.
  // Channel only (3.5b): a squad room has no tiers.
  const showTierBanner = gateKind !== 'team' && !!sessionId && effectiveMembersOnly && !viewerIsStreamer
    && viewerIsMember === true && chatTierLow(gate, memberInfo);
  const bannerMinTier = effectiveMembersOnly ? chatMinTierAbove(gate?.minTierRank, lowestTierRank) : null;
  // A gate minimum that is not a tier (the members-only chat perk is off):
  // no tier to name, no Switch tier.
  const gateTierKey = tierKeyOfRank(gate?.minTierRank);
  // Join and Switch tier only when a tier the channel sells can post at this
  // minimum (R2-2, ADV2-2): never with the perk off, never when every tier
  // from the minimum up is off while perk carriers keep the mode on. Never
  // for a member whose pending switch already reaches the minimum: every
  // switch lands at the renewal, so that switch is already the earliest way
  // in (the video lock's rule, R3C-REV-2; R5REV5-1).
  const gateReachable = !(viewerIsMember === true && pendingSwitchReaches(memberInfo, gateTierKey))
    && chatGateReachable(gate?.minTierRank, channelTiers);
  const bannerText = showTierBanner
    ? (chatTierLine(gateTierKey, onSwitchTier || !gateReachable ? 'plain' : 'switch') || CHAT_CANT_SEND)
    : (bannerMinTier ? chatTierLine(bannerMinTier, 'plain') : CHAT_MEMBERS_ONLY);

  return (
    <View style={[styles.chatPanel, style]}>
      <View style={styles.chatHeader}>
        {onCollapse ? (
          <TouchableOpacity
            onPress={onCollapse}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            accessibilityLabel="Collapse chat"
          >
            <Ionicons name="chevron-forward" size={16} color="#888" />
          </TouchableOpacity>
        ) : null}
        {showGateChip ? (
          // With the chip, the title takes one truncating line, so a narrow
          // OBS dock cuts the title, never the chip.
          <Text style={[styles.chatTitle, styles.chatTitleWithChip]} numberOfLines={1}>{title}</Text>
        ) : (
          <Text style={styles.chatTitle}>{title}</Text>
        )}
        {showGateChip ? (
          <TouchableOpacity
            style={[
              styles.gateChip,
              gateChipState === 'on' && styles.gateChipOn,
              gateChipState === 'paused' && styles.gateChipPaused,
            ]}
            onPress={() => setGateSheetOpen(true)}
            disabled={!gateChipInteractive}
            accessibilityRole={gateChipInteractive ? 'button' : 'text'}
            accessibilityLabel={
              gateChipState === 'paused' ? gateCopy.chipLabelPaused
                : gateChipState === 'on' ? gateCopy.chipLabel : gateCopy.chipLabelOff
            }
            accessibilityHint={gateChipState === 'paused' ? gateCopy.pausedHint : undefined}
          >
            {gateChipState === 'paused' ? (
              <Ionicons name="pause" size={10} color={COLORS.GOLD} style={{ marginRight: 4 }} />
            ) : null}
            <Text
              style={[styles.gateChipText, gateChipState === 'on' && styles.gateChipTextOn]}
              numberOfLines={1}
            >
              Members-only
            </Text>
          </TouchableOpacity>
        ) : null}
        {onPopout ? (
          <TouchableOpacity
            onPress={onPopout}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            accessibilityLabel="Pop out chat"
          >
            <Ionicons name="open-outline" size={15} color="#888" />
          </TouchableOpacity>
        ) : (
          <View style={{ width: 16 }} />
        )}
      </View>

      {pinned ? (
        <View style={styles.pinnedBar}>
          <Ionicons name="pin" size={12} color={COLORS.GOLD} style={{ marginRight: 6, marginTop: 1 }} />
          <Text style={styles.pinnedText} numberOfLines={2}>
            <Text style={styles.pinnedUser}>{pinned.user}</Text>
            {'  '}{pinned.message}
          </Text>
          {viewerIsStreamer ? (
            <TouchableOpacity
              onPress={unpinMessage}
              disabled={unpinBusy}
              hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
              accessibilityLabel="Unpin message"
            >
              <Ionicons name="close" size={13} color={unpinBusy ? '#444' : '#888'} />
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}

      {chatError ? <Text style={styles.chatErrorText}>{chatError}</Text> : null}

      <View style={{ flex: 1, minHeight: 0 }}>
        <ScrollView
          ref={scrollRef}
          style={styles.chatMessages}
          contentContainerStyle={{ paddingVertical: 8 }}
          showsVerticalScrollIndicator={false}
          onScroll={handleChatScroll}
          scrollEventThrottle={16}
          onContentSizeChange={handleChatContentSize}
        >
          {!sessionId ? (
            <Text style={styles.chatEmpty}>Chat opens when the stream is live.</Text>
          ) : messages.length === 0 ? (
            <Text style={styles.chatEmpty}>Say hi — be the first in chat.</Text>
          ) : (
            messages
              .filter((m) => !(hiddenUids && m.userId && hiddenUids.has(m.userId)))
              .map(renderMessage)
          )}
        </ScrollView>
        {hasUnseen ? (
          <TouchableOpacity style={styles.chatNewMsgs} onPress={jumpToLatest} accessibilityLabel="Jump to newest messages">
            <Ionicons name="arrow-down" size={12} color="#0b0b0b" style={{ marginRight: 5 }} />
            <Text style={styles.chatNewMsgsText}>New messages</Text>
          </TouchableOpacity>
        ) : null}
      </View>

      {/* Rumble / Super Crit live just above the input (founder 2026-07-09) —
          hidden on your own stream (you can't hype yourself). The button only
          opens the screen-level StreamRumble; nothing Rumble lives in here. */}
      {sessionId && !viewerIsStreamer && !hideActions && (rumblesEnabled || !hideSuperCrit) ? (
        <View style={styles.chatActionRow}>
          {rumblesEnabled ? (
            <TouchableOpacity style={styles.chatActionRumble} onPress={onRumble}>
              <Ionicons name="flash" size={13} color={COLORS.GOLD} style={{ marginRight: 5 }} />
              <Text style={styles.chatActionRumbleText}>Rumble</Text>
            </TouchableOpacity>
          ) : null}
          {!hideSuperCrit ? (
            <TouchableOpacity style={styles.chatActionSuper} onPress={onSuperCrit}>
              <Ionicons name="star" size={13} color="#0b0b0b" style={{ marginRight: 5 }} />
              <Text style={styles.chatActionSuperText}>Super Crit</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}

      {showMembersOnlyBanner || showTierBanner ? (
        <View style={styles.membersOnlyBanner}>
          <Ionicons name="lock-closed" size={12} color={COLORS.GOLD} style={{ marginRight: 6 }} />
          <Text style={styles.membersOnlyBannerText}>{gateKind === 'team' ? gateCopy.banner : bannerText}</Text>
          {showMembersOnlyBanner && onJoin && gateReachable ? (
            <TouchableOpacity
              style={styles.membersOnlyJoinBtn}
              onPress={() => onJoin(gateTierKey, 'membersChat')}
              accessibilityRole="button"
              {...(joinLabel ? { accessibilityLabel: joinLabel } : null)}
            >
              <Ionicons name="star-outline" size={12} color="#0b0b0b" style={{ marginRight: 4 }} />
              <Text style={styles.membersOnlyJoinText}>Join</Text>
            </TouchableOpacity>
          ) : null}
          {showTierBanner && onSwitchTier && gateTierKey && gateReachable ? (
            <TouchableOpacity style={styles.membersOnlyJoinBtn} onPress={() => onSwitchTier(gateTierKey)} accessibilityRole="button">
              <Ionicons name="swap-vertical" size={12} color="#0b0b0b" style={{ marginRight: 4 }} />
              <Text style={styles.membersOnlyJoinText}>{SWITCH_TIER_LABEL}</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}

      {!sessionId ? null : isLoggedIn ? (
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.chatInputRow}
        >
          <TextInput
            style={styles.chatInput}
            placeholder="Send a message…"
            placeholderTextColor="#666"
            value={inputText}
            onChangeText={setInputText}
            onSubmitEditing={handleSend}
            returnKeyType="send"
            maxLength={500}
          />
          <TouchableOpacity
            style={[styles.chatSendBtn, !inputText.trim() && { opacity: 0.4 }]}
            onPress={handleSend}
            disabled={!inputText.trim()}
          >
            <Ionicons name="send" size={16} color="#0b0b0b" />
          </TouchableOpacity>
        </KeyboardAvoidingView>
      ) : (
        <TouchableOpacity style={styles.chatSignupCta} onPress={() => navigation.navigate('SignUp')}>
          <Text style={styles.chatSignupCtaText}>Sign up to chat</Text>
          <Text style={styles.chatSignupCtaSub}>Watching is free — create an account to join in</Text>
        </TouchableOpacity>
      )}

      {/* Owner's members-only sheet (3.5a): turn on (confirmed), or turn off
          (always allowed; while paused it clears the kept mode). */}
      {gateChipInteractive ? (
        <Modal
          visible={gateSheetOpen}
          transparent
          animationType="fade"
          onRequestClose={() => { if (!gateBusy) setGateSheetOpen(false); }}
        >
          <Pressable style={styles.rumbleBackdrop} onPress={() => { if (!gateBusy) setGateSheetOpen(false); }}>
            <Pressable style={styles.rumbleCard} onPress={(e) => e.stopPropagation()}>
              <Text style={styles.rumbleTitle}>
                {gateChipState === 'paused' ? gateCopy.sheetTitlePaused : gateCopy.sheetTitle}
              </Text>
              {gateChipState === 'off' ? (
                <>
                  <Text style={styles.rumbleSub}>{gateCopy.onBody}</Text>
                  {newJoinsClosed ? (
                    <Text style={styles.rumbleSub}>{gateCopy.onClosed}</Text>
                  ) : null}
                </>
              ) : (
                <Text style={styles.rumbleSub}>
                  {gateChipState === 'paused' ? gateCopy.pausedHint : gateCopy.onNow}
                </Text>
              )}
              <TouchableOpacity
                style={[styles.rumbleSendBtn, gateBusy && { opacity: 0.6 }]}
                onPress={() => setMembersOnly(gateChipState === 'off')}
                disabled={gateBusy}
                accessibilityRole="button"
              >
                {gateBusy ? (
                  <ActivityIndicator size="small" color="#0b0b0b" />
                ) : (
                  <Text style={styles.rumbleSendBtnText}>{gateChipState === 'off' ? 'Turn on' : 'Turn off'}</Text>
                )}
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.superCancelBtn}
                onPress={() => setGateSheetOpen(false)}
                disabled={gateBusy}
              >
                <Text style={styles.superCancelBtnText}>Cancel</Text>
              </TouchableOpacity>
            </Pressable>
          </Pressable>
        </Modal>
      ) : null}
    </View>
  );
};

// ---------------------------------------------------------------------------
// MAIN SCREEN
// ---------------------------------------------------------------------------
export default function StreamWatchScreen({ navigation, route }) {
  // :channel is the PUBLIC userName (canonical URL segment since 2026-07-23 —
  // the uid in the watch URL was a leak); `userId` params from legacy
  // navigations/bookmarks still resolve. The server resolves either form and
  // returns the uid, which everything session-keyed below (chat, roster,
  // likes, rumbles) runs on.
  const channelParam = route?.params?.channel ?? route?.params?.userId;
  // What a channel card handed over (thumbnail, offline flag), so the
  // loading shell can draw the picture before getStreamPlayback answers.
  const cardHandoff = useMemo(() => peekStreamCard(channelParam), [channelParam]);
  const [userId, setUserId] = useState(null);
  const isFocused = useIsFocused();
  const functions = getFunctions();
  const { width, height } = useWindowDimensions();
  const isWide = width >= WIDE_BREAKPOINT;

  // Channel memberships (evergreen 3.5a). The viewer's own pair doc, live
  // (a constant "not a member" object when signed out or on your own
  // channel); declared above every effect that reads it. While it loads or
  // after a read error, membership is UNKNOWN: neither Join nor Member shows.
  const m = useMyChannelMembership(userId);
  // Static copy only (the verify banner, the turn-on confirm's pilot line).
  const membershipsFlag = useMembershipsLive();
  const membershipsFlagLive = membershipsFlag?.live === true;
  // What the Join modal was opened for, {kind, id, name} (3.5b: a channel,
  // or the channel owner's team from the upsell row), and the page (channel
  // uid) it was opened on (the Rumble picker's pattern): a channel switch
  // hides the modal and its key change tears down the payment form.
  const [joinOpenFor, setJoinOpenFor] = useState(null);
  const joinPageRef = useRef(null);
  // Tiers (R5): open the modal at its switch stage (a member below the
  // chat's minimum tier taps "Switch tier"); null = the join panel.
  const [joinOpenTo, setJoinOpenTo] = useState(null);
  const [joinMinTier, setJoinMinTier] = useState(null);
  // The perk the opening chat gate needs ('membersChat'; null = the engage row).
  const [joinGatePerk, setJoinGatePerk] = useState(null);
  const [memberPanelOpen, setMemberPanelOpen] = useState(false);

  const [stream, setStream] = useState(null);
  // Team memberships (evergreen 3.5b): the channel owner's team for the
  // "Also streams with Team X" row (getStreamPlayback's team field; an
  // older server omits it = no row), and the viewer's own pair doc for that
  // team (a constant "not a member" object when there is no team or the
  // viewer is signed out; unknown while loading or after a read error).
  const streamTeam = stream?.team && typeof stream.team === 'object'
    && typeof stream.team.teamId === 'string' && stream.team.teamId ? stream.team : null;
  const tm = useMyMembership('team', streamTeam ? streamTeam.teamId : null);
  // Offline channel state (evergreen Step 1.2): recent published videos so
  // the channel page has something to watch when the creator is not live.
  // Refetched when the viewer becomes a member here (3.5a), so the lock
  // chips on members-only videos clear without a reload.
  const [offlineVideos, setOfflineVideos] = useState([]);
  useEffect(() => {
    if (!stream || stream.live || !stream.userId) { setOfflineVideos([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable(getFunctions(), 'getUserPublicVideos');
        const { data } = await fn({ uid: stream.userId });
        if (!cancelled) setOfflineVideos((data?.videos || []).slice(0, 6));
      } catch (_) { if (!cancelled) setOfflineVideos([]); }
    })();
    return () => { cancelled = true; };
  }, [stream?.userId, stream?.live, m.isMember]);
  const [loading, setLoading] = useState(true);
  const [nowMs, setNowMs] = useState(Date.now());
  const [likeBusy, setLikeBusy] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  // Rumble (evergreen 3.3): the channel uid the picker was opened for, not a
  // boolean, so a channel switch can never carry an open picker (or its
  // checkout) over to a new streamer. StreamRumble owns everything else.
  const [rumbleOpenFor, setRumbleOpenFor] = useState(null);
  // Super Crit: real-money highlighted chat message (YouTube Super Chat model)
  const [showSuper, setShowSuper] = useState(false);
  const [superSetup, setSuperSetup] = useState(null);
  const [superTier, setSuperTier] = useState('sc_500');
  const [superMsg, setSuperMsg] = useState('');
  const [superBusy, setSuperBusy] = useState(false);
  const [superError, setSuperError] = useState(null);
  const [superSent, setSuperSent] = useState(false);
  // Embedded Stripe checkout (first-time / SCA / no saved card)
  const [superEmbedded, setSuperEmbedded] = useState(null); // { clientSecret, publishableKey, cardCheck, purchaseId } | null
  const superCheckoutContainerRef = useRef(null);
  const superCheckoutInstanceRef = useRef(null);
  // Purchase limits ('limits' mode only; 'cap' sends no limit object and every
  // notice stays null): the setup's room, the saved card's status, refusals.
  const superLimit = superSetup?.limit && typeof superSetup.limit === 'object' ? superSetup.limit : null;
  const superSavedStatus = savedCardStatus(superLimit);
  const superTips = useTipLimitNotices(superLimit?.verify);
  const playerContainerRef = useRef(null);
  // Moderation + reporting (founder 2026-07-10): tap a chat handle → user
  // card (profile / hide / mute / report); flag button reports the stream.
  const [chatUserCard, setChatUserCard] = useState(null); // { user, userId, messageId, messageText } | null
  const [hiddenUids, setHiddenUids] = useState(() => new Set()); // local hide, this visit only
  const [mutedUids, setMutedUids] = useState(() => new Set()); // streamer mutes (session UI state)
  const [modBusy, setModBusy] = useState(false);
  const [reportTarget, setReportTarget] = useState(null); // { kind, uid, name, messageId, messageText } | null
  const [reportReason, setReportReason] = useState('inappropriate');
  const [reportDetails, setReportDetails] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const [reportStatus, setReportStatus] = useState(null); // 'sent' | error text
  const [myName, setMyName] = useState(null); // public handle for chat attribution
  // Twitch-style collapsible panels (persisted): left live-channels rail
  // collapses to avatars-only; chat collapses to a slim expand handle.
  const [railCollapsed, setRailCollapsed] = useState(() => readPref('cc_rail_collapsed', false));
  const [chatCollapsed, setChatCollapsed] = useState(() => readPref('cc_chat_collapsed', false));
  // Player audio + active element. isMuted OUTLIVES the player: the
  // live→offline ternary unmounts LiveHlsPlayer entirely, and a mid-watch
  // src swap (channel-key rotation, LiveRail hop) flips elements — state
  // here keeps the viewer's unmute across both.
  const [isMuted, setIsMuted] = useState(true);
  const [activeVideoEl, setActiveVideoEl] = useState(null);
  const anonSessionId = useRef(`s_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`);

  const toggleRail = () => setRailCollapsed((v) => { writePref('cc_rail_collapsed', !v); return !v; });
  const toggleChat = () => setChatCollapsed((v) => { writePref('cc_chat_collapsed', !v); return !v; });

  // Browser-size-aware player: the full 16:9 frame must fit the viewport
  // (minus header/nav/info chrome and whatever the side panels occupy)
  // instead of swallowing the page.
  const railW = isWide ? (railCollapsed ? RAIL_COLLAPSED_WIDTH : RAIL_WIDTH) : 0;
  const chatW = isWide ? (chatCollapsed ? CHAT_COLLAPSED_WIDTH : CHAT_WIDTH) : 0;
  const contentWidth = isWide ? width - railW - chatW - 48 : width - 32;
  const playerWidth = Math.max(320, Math.min(contentWidth, ((height - 260) * 16) / 9));

  const fetchStream = useCallback(async () => {
    if (!channelParam) return;
    try {
      const fn = httpsCallable(functions, 'getStreamPlayback');
      const result = await fn({ channel: channelParam });
      const data = result.data || null;
      setStream(data);
      if (data?.userId) setUserId(data.userId);
      // Keep the live DOM metadata in sync: sender-side share sheets (share
      // button while ON this page) read document.title + the og tags from the
      // DOM, not from the crawler-served HTML. Mirrors serveSocialPreview.
      if (data) {
        const name = data.displayName || 'CritCash streamer';
        setPageMeta({
          title: data.live ? `🔴 ${name} is LIVE on CritCash` : `${name} on CritCash`,
          description: data.title ||
            (data.live
              ? `${name} is streaming live right now — watch on CritCash.`
              : `Watch ${name}'s channel on CritCash.`),
          url: `https://www.critcash.com/s/${encodeURIComponent(data.userName || channelParam)}`,
          image: data.thumbnailUrl || undefined,
        });
      }
      // Landed via a legacy /s/<uid> link? Swap the address bar to the
      // canonical handle without a nav-state churn (web only).
      if (
        Platform.OS === 'web' && typeof window !== 'undefined' &&
        data?.userName && data.userName !== channelParam
      ) {
        try {
          window.history.replaceState(null, '', `/s/${encodeURIComponent(data.userName)}`);
        } catch (_) { /* cosmetic only */ }
      }
    } catch (err) {
      console.warn('[StreamWatch] playback fetch failed:', err.message);
      setStream(null);
    } finally {
      setLoading(false);
    }
  }, [functions, channelParam]);

  useEffect(() => {
    if (!isFocused) return;
    fetchStream();
    const poll = setInterval(fetchStream, 45000);
    const tick = setInterval(() => setNowMs(Date.now()), 30000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, [isFocused, fetchStream]);

  // Restore the default share card when leaving the channel page, so sharing
  // from a later screen doesn't carry this streamer's og tags. Keyed on FOCUS,
  // not unmount — "Back to Streams" can push (screen stays mounted under the
  // stack) when the watch page was the entry URL. Refocus re-applies via
  // fetchStream (its poll effect is focus-gated).
  useEffect(() => {
    if (!isFocused) resetPageMeta();
    return () => resetPageMeta();
  }, [isFocused]);

  // Rules-metered watch roster (clone of the EventWatch rosterBeat): month-
  // keyed parent doc, +20s per beat AFTER the arrival beat, create shape must
  // match firestore.rules exactly. Denied/too-soon beats silently drop.
  useEffect(() => {
    if (!isFocused || !userId || !stream?.live) return;

    const monthKey = (() => {
      const d = new Date();
      return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    })();
    const rosterParent = `${userId}_${monthKey}`;
    const rosterTouched = { current: false };

    const rosterBeat = () => {
      const uid = auth.currentUser?.uid;
      if (uid === userId) return; // self-views never meter
      const rosterRef = uid
        ? doc(db, 'streamAudience', rosterParent, 'viewers', uid)
        : doc(db, 'streamAudience', rosterParent, 'anon', anonSessionId.current);
      const delta = rosterTouched.current ? 20 : 0;
      updateDoc(rosterRef, {
        lastSeenAt: serverTimestamp(),
        watchSeconds: increment(delta),
      }).then(() => { rosterTouched.current = true; }).catch(() => {
        setDoc(rosterRef, {
          firstSeenAt: serverTimestamp(),
          lastSeenAt: serverTimestamp(),
          watchSeconds: 0,
        }).then(() => { rosterTouched.current = true; }).catch(() => {});
      });
    };

    rosterBeat();
    const interval = setInterval(rosterBeat, 20000);
    const onVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') rosterBeat();
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isFocused, userId, stream?.live]);

  // Public handle for chat attribution. public_profiles.userName is the
  // canonical public name — the auth/users displayName can be the person's
  // REAL name and never touches chat.
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    getDoc(doc(db, 'public_profiles', uid)).then((snap) => {
      const n = snap.exists() ? snap.data().userName : null;
      if (n) setMyName(n);
    }).catch(() => {});
    const unsub = onSnapshot(doc(db, 'users', uid), (snap) => {
      const d = snap.data() || {};
      setMyName((prev) => prev || d.userName || null);
    }, () => {});
    return () => unsub();
  }, []);

  // ---------------------------------------------------------------------
  // Rumble (evergreen 3.3) — real money via Stripe, one purchase to this
  // channel. The chat button is the only door and shows on the server's
  // rumblesEnabled; StreamRumble (meter, picker, burst) does the rest.
  // Stable target + callbacks: StreamRumble's checkout mount must not tear
  // down on every screen re-render.
  // ---------------------------------------------------------------------
  const rumbleTarget = useMemo(
    () => (userId ? { targetType: 'stream', targetId: userId } : null),
    [userId]
  );
  const canRumble = stream?.rumblesEnabled === true && !!stream?.live;

  const openRumble = () => {
    // A stale tap between polls: the chat hides the button on the same field.
    if (!canRumble || !userId) return;
    if (!auth.currentUser) { navigation.navigate('SignUp'); return; }
    // Off web the picker explains itself (web-app only), so no silent return.
    setRumbleOpenFor(userId);
  };

  const closeRumble = useCallback(() => setRumbleOpenFor(null), []);
  const rumbleRequireAuth = useCallback(() => navigation.navigate('SignUp'), [navigation]);

  // ---------------------------------------------------------------------
  // Super Crit — real money via Stripe (flood-pipeline posture: one-tap on a
  // saved card, embedded checkout fallback that saves the card for next time)
  // ---------------------------------------------------------------------
  const openSuperCrit = async () => {
    // Evergreen 3.2: Super Crits credit the streamer directly, so they only
    // open on a verified channel. The chat hides the button on the same flag;
    // this catches a stale tap between polls. Strict === false: a server that
    // predates the flag omits it, and that must keep today's behavior.
    if (stream?.tipsEnabled === false) return;
    if (!auth.currentUser) { navigation.navigate('SignUp'); return; }
    if (Platform.OS !== 'web') return;
    setSuperError(null);
    setSuperSent(false);
    setSuperEmbedded(null);
    superTips.reset();
    setShowSuper(true);
    try {
      const fn = httpsCallable(functions, 'getSuperCritSetup');
      const result = await fn();
      setSuperSetup(result.data || null);
      // 'limits' mode: a tier above the room is disabled, so start on the
      // largest one that fits.
      const lim = result.data?.limit;
      const tiers = Array.isArray(result.data?.tiers) ? result.data.tiers : null;
      if (lim && tiers) {
        setSuperTier((cur) => {
          const curTier = tiers.find((t) => t.id === cur);
          if (!curTier || fitsLimit(lim, curTier.cents)) return cur;
          const fit = tiers.filter((t) => fitsLimit(lim, t.cents)).sort((a, b) => b.cents - a.cents)[0];
          return fit ? fit.id : cur;
        });
      }
    } catch (err) {
      console.warn('[StreamWatch] super crit setup failed:', err.message);
      setSuperSetup(null);
    }
  };

  // 'limits' mode: a cooldown ended, so re-read the room (no reset).
  const refreshSuperSetup = async () => {
    try {
      const fn = httpsCallable(functions, 'getSuperCritSetup');
      const result = await fn();
      setSuperSetup(result.data || null);
      superTips.setLimitErr(null);
    } catch (err) {
      console.warn('[StreamWatch] super crit setup refresh failed:', err.message);
    }
  };

  const closeSuperCrit = useCallback(() => {
    try { superCheckoutInstanceRef.current?.destroy(); } catch (_) { /* noop */ }
    superCheckoutInstanceRef.current = null;
    setSuperEmbedded(null);
    setShowSuper(false);
    setSuperMsg('');
    superTips.reset();
  }, [superTips.reset]); // eslint-disable-line react-hooks/exhaustive-deps

  // The verify button (L11) closes the modal first: a React Native Web modal
  // stays on screen over the next route otherwise.
  const verifyFromSuper = () => {
    closeSuperCrit();
    navigation.navigate('IdVerification');
  };

  const startSuperEmbedded = async () => {
    const fn = httpsCallable(functions, 'createSuperCritCheckout');
    const result = await fn({
      streamerUid: userId, tierId: superTier, message: superMsg, sessionId: chatSessionId,
    });
    if (!result.data?.clientSecret) throw new Error('Could not start checkout.');
    setSuperEmbedded({
      clientSecret: result.data.clientSecret,
      publishableKey: result.data.publishableKey || superSetup?.publishableKey,
      cardCheck: result.data.cardCheck === true,
      purchaseId: typeof result.data.purchaseId === 'string' ? result.data.purchaseId : null,
    });
  };

  const selectedSuperCents = (superSetup?.tiers || []).find((t) => t.id === superTier)?.cents
    ?? { sc_100: 100, sc_300: 300, sc_500: 500, sc_1000: 1000, sc_2500: 2500, sc_5000: 5000, sc_10000: 10000, sc_50000: 50000 }[superTier];
  // 'limits' mode: at the limit, or the picked tier is above the room.
  const superLimitBlocks = !!superLimit
    && (!!limitLines(superLimit)?.atLimit || !fitsLimit(superLimit, selectedSuperCents));

  const sendSuperCrit = async () => {
    if (superBusy || superLimitBlocks) return;
    setSuperBusy(true);
    setSuperError(null);
    superTips.clearNotices();
    try {
      // 'limits' mode: a saved card the setup says can't be used here goes
      // straight to checkout (L5s / L6 shows in the panel).
      if (superSetup?.hasSavedCard && superSavedStatus === 'ok') {
        try {
          // 130s sits above the server's timeoutSeconds 120, so the server's
          // success or tips-pending reply (or Cloud Run's 504) always arrives
          // before the SDK's own deadline (70s by default) gives up first.
          const fn = httpsCallable(functions, 'superCritOneTap', { timeout: 130000 });
          await fn({ streamerUid: userId, tierId: superTier, message: superMsg, sessionId: chatSessionId });
          setSuperSent(true);
          setTimeout(closeSuperCrit, 1600);
          return;
        } catch (err) {
          const raw = String(err?.message || '');
          const code = String(err?.code || '');
          // Outcome unknown: the SDK's own deadline, Cloud Run's 504 at 120s
          // (maps to deadline-exceeded), or a dropped connection (fetch
          // failure = code internal, message exactly 'internal'). The saved
          // card may have been charged, so this reads as tips-pending: a NEW
          // error with the server's own sentence, because rethrowing err would
          // show the bare 'deadline-exceeded' after the colon-strip below.
          // Not 'functions/unavailable' (a definite pre-charge refusal that
          // keeps its own text) and not the server's uppercase 'INTERNAL'
          // (unexpected server throws happen only before the charge).
          if (code === 'functions/deadline-exceeded' || (code === 'functions/internal' && raw === 'internal')) {
            throw new Error("tips-pending: We couldn't confirm your Super Crit yet. Please wait a minute before sending another.");
          }
          // Never a second charge attempt on these (evergreen 3.2):
          //  - tips-unavailable: the channel is not open for Super Crits, so
          //    checkout would be refused the same way.
          //  - tips-pending: the saved card MAY have been charged and the
          //    server could not confirm it yet; opening checkout could charge
          //    the fan twice. If the charge went through, the webhook
          //    backstop (payment_intent.succeeded) records it; the fan waits.
          // Both surface through the catch below: the spinner stops and the
          // text after the first colon shows in the modal.
          if (raw.includes('tips-unavailable') || raw.includes('tips-pending')) {
            throw err;
          }
          // 'limits' mode with card checks on: the saved card can't be used
          // here (card-limit / card-blocked; nothing was charged). Fall
          // through to embedded checkout with L5 / L6 shown above it.
          const cardStatus = cardStatusFromError(err);
          if (cardStatus) superTips.setCardRefusal(cardStatus);
          // Saved card declined / needs SCA → fall through to embedded checkout.
          else if (!raw.includes('authentication-required') && !raw.includes('no-saved-card')) {
            throw err;
          }
        }
      }
      await startSuperEmbedded();
    } catch (err) {
      const raw = String(err?.message || '');
      // 'limits' mode: a tips-limit refusal carries the room (L2-L4).
      const lim = limitFromError(err);
      if (lim) superTips.setLimitErr({ limit: lim, amountCents: selectedSuperCents });
      // Server refusals follow 'token: message' (e.g. 'tips-unavailable: ...'),
      // so stripping through the first colon leaves the fan-facing sentence.
      else setSuperError(raw.replace(/^.*?:/, '').trim() || 'Could not send the Super Crit.');
    } finally {
      setSuperBusy(false);
    }
  };

  // Mount the embedded checkout whenever we have a clientSecret + container
  // (same mount/teardown discipline as TsunamiMeter / GiftCardScreen).
  useEffect(() => {
    if (Platform.OS !== 'web' || !superEmbedded?.clientSecret) return;
    let cancelled = false;

    (async () => {
      try {
        if (typeof superEmbedded.publishableKey !== 'string' || !superEmbedded.publishableKey) {
          throw new Error('Stripe publishable key is not configured.');
        }
        const { loadStripe } = await import('@stripe/stripe-js');
        const stripe = await loadStripe(superEmbedded.publishableKey);
        if (cancelled || !stripe) return;

        const instance = await stripe.createEmbeddedCheckoutPage({
          clientSecret: superEmbedded.clientSecret,
          onComplete: () => {
            // Paid — the webhook drops the highlighted message into chat.
            try { superCheckoutInstanceRef.current?.destroy(); } catch (_) { /* noop */ }
            superCheckoutInstanceRef.current = null;
            const held = superEmbedded.cardCheck === true && superEmbedded.purchaseId ? superEmbedded.purchaseId : null;
            setSuperEmbedded(null);
            if (!held) {
              setSuperSent(true);
              setTimeout(closeSuperCrit, 1600);
              return;
            }
            // Card checks on ('limits' mode): only authorized until the
            // server checks the card, so confirm before saying sent (3.7).
            superTips.confirmHeld('super_crit', held, {
              onCompleted: () => { setSuperSent(true); setTimeout(closeSuperCrit, 1600); },
              onFailed: () => setSuperError('Could not send the Super Crit.'),
            });
          },
        });

        if (cancelled) { try { instance.destroy(); } catch (_) { /* noop */ } return; }
        superCheckoutInstanceRef.current = instance;
        if (superCheckoutContainerRef.current) {
          instance.mount(superCheckoutContainerRef.current);
        }
      } catch (err) {
        console.error('[StreamWatch] embedded checkout mount failed:', err);
        if (!cancelled) {
          setSuperEmbedded(null);
          setSuperError('Could not open the payment form. Please try again.');
        }
      }
    })();

    return () => {
      cancelled = true;
      try { superCheckoutInstanceRef.current?.destroy(); } catch (_) { /* noop */ }
      superCheckoutInstanceRef.current = null;
    };
  }, [superEmbedded, closeSuperCrit]);

  const toggleLike = async () => {
    if (!auth.currentUser) { navigation.navigate('SignUp'); return; }
    if (likeBusy) return;
    setLikeBusy(true);
    try {
      const fn = httpsCallable(functions, 'toggleStreamLike');
      const result = await fn({ userId });
      setStream((prev) => prev ? { ...prev, liked: result.data.liked, likeCount: result.data.likeCount } : prev);
    } catch (err) {
      console.warn('[StreamWatch] like failed:', err.message);
    } finally {
      setLikeBusy(false);
    }
  };

  const toggleFollow = async () => {
    if (!auth.currentUser) { navigation.navigate('SignUp'); return; }
    if (followBusy) return;
    setFollowBusy(true);
    try {
      const fn = httpsCallable(functions, 'toggleStreamFollow');
      const result = await fn({ userId });
      setStream((prev) => prev ? { ...prev, following: result.data.following, followerCount: result.data.followerCount } : prev);
    } catch (err) {
      console.warn('[StreamWatch] subscribe failed:', err.message);
    } finally {
      setFollowBusy(false);
    }
  };

  const goBackToStreams = () => {
    if (navigation.canGoBack()) navigation.goBack();
    else navigation.navigate('Streams');
  };

  // Chat-only popout window (web): no player, no HLS bandwidth — droppable
  // into an OBS Custom Browser Dock or a second monitor (founder 2026-07-23).
  const openChatPopout = () => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const handle = stream?.userName || channelParam;
    window.open(
      `/chat/${encodeURIComponent(handle)}`,
      'cc_chat_popout',
      'width=400,height=680,noopener'
    );
  };

  // ---------------------------------------------------------------------
  // Moderation + reporting handlers
  // ---------------------------------------------------------------------
  // Chat handles ARE the public userName (public_profiles) — the profile
  // route resolves by userName. The placeholder name has no profile.
  const openProfile = (userName) => {
    if (!userName || userName === 'CritCash streamer' || userName === 'Player' || userName === 'Someone') return;
    navigation.navigate('PublicProfile', { userName });
  };

  const toggleHideUser = (targetUid) => {
    setHiddenUids((prev) => {
      const next = new Set(prev);
      if (next.has(targetUid)) next.delete(targetUid);
      else next.add(targetUid);
      return next;
    });
    setChatUserCard(null);
  };

  const toggleMuteUser = async (targetUid) => {
    if (modBusy || !chatSessionId) return;
    setModBusy(true);
    try {
      const fnName = mutedUids.has(targetUid) ? 'unmuteStreamChatUser' : 'muteStreamChatUser';
      await httpsCallable(functions, fnName)({ sessionId: chatSessionId, userId: targetUid });
      setMutedUids((prev) => {
        const next = new Set(prev);
        if (next.has(targetUid)) next.delete(targetUid);
        else next.add(targetUid);
        return next;
      });
      setChatUserCard(null);
    } catch (err) {
      console.warn('[StreamWatch] mute toggle failed:', err.message);
    } finally {
      setModBusy(false);
    }
  };

  const openReport = (kind, target) => {
    if (!auth.currentUser) { navigation.navigate('SignUp'); return; }
    setReportReason('inappropriate');
    setReportDetails('');
    setReportStatus(null);
    setReportTarget({ kind, ...target });
    setChatUserCard(null);
  };

  const submitReport = async () => {
    if (reportBusy || !reportTarget) return;
    setReportBusy(true);
    setReportStatus(null);
    try {
      await httpsCallable(functions, 'reportStreamContent')({
        kind: reportTarget.kind,
        streamerUid: userId,
        targetUid: reportTarget.uid,
        sessionId: chatSessionId,
        messageId: reportTarget.messageId || null,
        messageText: reportTarget.messageText || null,
        reason: reportReason,
        details: reportDetails,
      });
      setReportStatus('sent');
      setTimeout(() => setReportTarget(null), 1600);
    } catch (err) {
      const raw = String(err?.message || '');
      setReportStatus(raw.includes('already-reported')
        ? "You've already reported this — our team is on it."
        : 'Could not send the report. Please try again.');
    } finally {
      setReportBusy(false);
    }
  };

  const isSelf = auth.currentUser?.uid === userId;

  // ---------------------------------------------------------------------
  // Channel memberships (evergreen 3.5a). "Join" is the membership verb
  // ("Subscribe"/subscribeBtn* mean Follow here). ONE predicate for Join, used
  // by the engage row and the chat's members-only banner, so they never
  // disagree and never offer a Join the server would refuse. Signed out, the
  // hook's constant object shows Join (tap -> SignUp), never Member.
  // ---------------------------------------------------------------------
  const showJoin = stream?.membershipsEnabled === true && !m.loading && !m.error && !m.isMember;
  const showMember = m.isMember === true;

  // The channel's public name for the modal, kept through a failed poll
  // (stream -> null) so an open modal never loses its title.
  const joinNameRef = useRef(null);
  if (stream?.userId && stream.userId === userId && stream.displayName) joinNameRef.current = stream.displayName;
  // minTierKey (R5): the chat banner passes the members-only chat's minimum
  // tier and its perk ('membersChat') so the modal preselects a tier that can
  // chat (a press event from the engage row is not a tier key and is ignored).
  const openJoin = (minTierKey, gatePerk) => {
    if (!userId) return;
    if (!auth.currentUser) { navigation.navigate('SignUp'); return; }
    joinPageRef.current = userId;
    setJoinOpenTo(null);
    setJoinMinTier(isTierKey(minTierKey) ? minTierKey : null);
    setJoinGatePerk(gatePerk === 'membersChat' ? gatePerk : null);
    setJoinOpenFor({ kind: 'channel', id: userId, name: joinNameRef.current });
  };
  // Only the chat banner opens the switch stage here.
  const openSwitchTier = (minTierKey) => {
    if (!userId || !auth.currentUser) return;
    joinPageRef.current = userId;
    setJoinOpenTo('switch');
    setJoinMinTier(isTierKey(minTierKey) ? minTierKey : null);
    setJoinGatePerk('membersChat');
    setJoinOpenFor({ kind: 'channel', id: userId, name: joinNameRef.current });
  };
  const closeJoin = useCallback(() => {
    setJoinOpenFor(null); setJoinOpenTo(null); setJoinMinTier(null); setJoinGatePerk(null);
  }, []);
  // The Join modal's target: what it was opened for, while this is still the
  // page it was opened on (the 3.5a joinOpenFor === userId rule); closed, the
  // channel's own target keeps it mounted as in 3.5a (memoized, so a closed
  // modal sees a stable target).
  const joinName = joinNameRef.current;
  const channelJoinTarget = useMemo(
    () => (userId ? { kind: 'channel', id: userId, name: joinName } : null),
    [userId, joinName],
  );
  const joinTarget = joinOpenFor && joinPageRef.current === userId ? joinOpenFor : null;
  const joinMountTarget = joinTarget || channelJoinTarget;

  // Team memberships (evergreen 3.5b): "Also streams with Team X. Join Team
  // X." under the engage row, never inside the chat. ONE predicate: the
  // server hint (false for a viewer on the roster and for the owner, and
  // while team memberships are dark for this viewer) and the viewer's own
  // team pair doc (neither row while it loads or errs). A member sees the
  // quieter "Also streams with Team X." linking to the team page. Signed
  // out, the hook's constant object shows Join (tap -> SignUp). Money-free.
  const showTeamUpsell = streamTeam?.membershipsEnabled === true && !tm.loading && !tm.error && !tm.isMember;
  const showTeamMemberRow = !!streamTeam && tm.isMember === true;
  const teamNoun = teamDisplayName(streamTeam?.name);
  const openTeamJoin = () => {
    // A stale tap between polls: the row hides on the same predicate.
    if (!showTeamUpsell || !streamTeam || !userId) return;
    if (!auth.currentUser) { navigation.navigate('SignUp'); return; }
    joinPageRef.current = userId;
    setJoinOpenTo(null);
    setJoinMinTier(null);
    setJoinGatePerk(null);
    setJoinOpenFor({ kind: 'team', id: streamTeam.teamId, name: streamTeam.name });
  };
  const openTeamPage = () => {
    if (!streamTeam) return;
    navigation.navigate('TeamPage', { teamId: streamTeam.teamId });
  };

  // What the chat needs from the viewer's own pair doc (the hook's ms
  // fields; no raw Timestamp). Memoized so StreamChat's effects stay quiet.
  const memberDoc = m.doc || null;
  const memberInfo = useMemo(() => (memberDoc ? {
    status: memberDoc.status,
    activeUntilMs: Number(memberDoc.activeUntilMs) || 0,
    updatedAtMs: Number(memberDoc.updatedAtMs) || 0,
    removedByCreator: memberDoc.removedByCreator === true,
    badgePerk: memberDoc.perks?.badge === true,
    // Tiers (R5): the rule's memberTierOk inputs.
    tierKey: memberDoc.tierKey || null,
    tierRank: Number(memberDoc.tierRank) || 1,
    chatPerk: memberDoc.perks?.membersChat === true,
    // A pending switch's tier (pendingSwitchReaches, R5REV5-1).
    nextTierKey: memberDoc.nextTierKey || null,
  } : null), [
    memberDoc?.status, memberDoc?.activeUntilMs, memberDoc?.updatedAtMs,
    memberDoc?.removedByCreator, memberDoc?.perks?.badge,
    memberDoc?.tierKey, memberDoc?.tierRank, memberDoc?.perks?.membersChat,
    memberDoc?.nextTierKey,
  ]);
  // null = unknown (loading or read error): the chat never draws the banner
  // then and lets the rule decide. A removed member has no seat (5.11b).
  const viewerIsMember = m.loading || m.error ? null : m.canChatAsMember === true;
  const membershipsLive = typeof stream?.membershipsLive === 'boolean' ? stream.membershipsLive : null;
  // The owner's members-only chip (StreamChat also shows it while the gate
  // is on). The confirm adds the "can't join right now" line when new
  // members are paused or memberships are dark (a pilot).
  const canToggleMembersOnly = isSelf && (
    stream?.membershipSelf?.membersChatAvailable === true || stream?.membershipSelf?.paused === true
  );
  const newJoinsClosed = stream?.membershipSelf?.paused === true || !membershipsFlagLive;
  const toggleMembersOnlyChat = useCallback((on) => setChannelChatMembersOnly({ on: on === true }), []);

  // The info row is capped at playerWidth; below ENGAGE_WRAP_MIN_PX (and on
  // every narrow layout) the engage row takes its own full-width line.
  const engageWrap = !isWide || playerWidth < ENGAGE_WRAP_MIN_PX;

  // Member panel: the perks this member actually has (a removed member keeps
  // members-only videos only, until the paid month ends).
  const memberPerks = memberDoc
    ? deliveredPerks(memberDoc.perks || {}, memberDoc.removedByCreator === true) || {}
    : {};
  const memberPerkKeys = LISTED_PERKS.filter((k) => memberPerks[k] === true);
  const openManageMemberships = () => {
    setMemberPanelOpen(false);
    navigation.navigate('Memberships');
  };

  // Switch tier in the chat only for a member who could switch now (the
  // profile card's rule, R3C-3): never set to end, never inside 2 hours of the
  // renewal, never on a channel that sells nothing to them (R3C-1).
  const memberCanSwitch = showMember && !isSelf && profileCanSwitch(m.doc, stream?.membershipTiers);

  // Everything the chat needs for memberships, the same in both layouts.
  const membershipChatProps = {
    gateKey: userId,
    membershipsLive,
    viewerIsMember,
    memberInfo,
    onJoin: showJoin ? openJoin : null,
    badgeCreatorUid: userId,
    canToggleMembersOnly,
    onToggleMembersOnly: isSelf ? toggleMembersOnlyChat : null,
    newJoinsClosed,
    // Tiers (R5): the lowest tier the channel offers (the server's sellable
    // list), and "Switch tier" for a member below the chat's minimum who
    // could switch now. channelTiers is [] for a member who could not, or a
    // channel selling nothing, so no line tells them to switch (R3C-1, R3C-3);
    // for a member it never counts their own or pending tier (R3C-REV-1).
    lowestTierRank: lowestOnRank(stream?.membershipTiers),
    onSwitchTier: memberCanSwitch ? openSwitchTier : null,
    channelTiers: chatChannelTiers(stream, showMember, memberCanSwitch, m.doc),
  };
  // Tap a chat handle -> user card; a live member badge adds its tenure and tier.
  const openChatUserCard = (msg, memberBadge) => setChatUserCard({
    user: msg.user,
    userId: msg.userId,
    messageId: msg.id,
    messageText: msg.message,
    memberMonths: memberBadge ? (Number(memberBadge.m) || 0) : null,
    memberTierKey: memberBadge && isTierKey(memberBadge.tk) ? memberBadge.tk : null,
  });

  // Chat room id comes from the server (2026-07-23): a grace-windowed epoch
  // that survives OBS restarts/drops, so refresh keeps the chat + pin. The
  // server also keeps it shipping for a few minutes after the stream goes
  // offline, so chat rides through brief drops instead of closing. Old
  // `${uid}_${relay startedAt}` computation kept as fallback for a cached
  // pre-epoch backend response.
  const chatSessionId = stream
    ? (stream.chatSessionId || (stream.live && stream.startedAtMs ? `${userId}_${stream.startedAtMs}` : null))
    : null;

  const renderMainColumn = () => (
    <>
      {/* In-a-match banner — the track switch, surfaced */}
      {stream.inMatch?.eventId ? (
        <TouchableOpacity
          style={[styles.matchBanner, { maxWidth: playerWidth }]}
          onPress={() => navigation.navigate('EventWatch', { eventId: stream.inMatch.eventId })}
        >
          <Ionicons name="trophy" size={16} color={COLORS.GOLD} style={{ marginRight: 8 }} />
          <Text style={styles.matchBannerText} numberOfLines={1}>
            {stream.displayName} is competing in {stream.inMatch.name} — watch the match
          </Text>
          <Ionicons name="chevron-forward" size={16} color={COLORS.GOLD} />
        </TouchableOpacity>
      ) : null}

      {/* Player (size-capped) */}
      <View ref={playerContainerRef} style={[styles.playerBox, { width: playerWidth }]}>
        {stream.live && stream.playbackUrl ? (
          <>
            <LiveHlsPlayer
              src={stream.playbackUrl}
              muted={isMuted}
              onActiveVideoChange={setActiveVideoEl}
              onAutoplayBlocked={() => setIsMuted(true)}
              poster={stream.thumbnailUrl || undefined}
            />
            <LivePlayerOverlay
              videoEl={activeVideoEl}
              containerRef={playerContainerRef}
              muted={isMuted}
              onMuteToggle={() => setIsMuted((m) => !m)}
              showStarting
            />
          </>
        ) : (
          <View style={styles.offlineVideo}>
            {stream.thumbnailUrl ? (
              <Image source={{ uri: stream.thumbnailUrl }} style={styles.offlineThumb} resizeMode="cover" />
            ) : null}
            <View style={styles.offlineOverlay}>
              <Text style={styles.offlineTitle}>{stream.displayName} is offline</Text>
              {stream.nextStreamAtMs ? (
                <Text style={styles.offlineNext}>Next stream: {new Date(stream.nextStreamAtMs).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}</Text>
              ) : stream.lastLiveMs ? (
                <Text style={styles.offlineLast}>Last live {new Date(stream.lastLiveMs).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</Text>
              ) : null}
              {offlineVideos.length ? (
                <View style={styles.offlineShelf}>
                  {offlineVideos.map((v) => (
                    <TouchableOpacity key={v.id} style={styles.offlineVideoCard} onPress={() => navigation.navigate('VideoWatch', { videoId: v.id })} activeOpacity={0.85}>
                      {v.thumbnailUrl ? <Image source={{ uri: v.thumbnailUrl }} style={styles.offlineVideoThumb} resizeMode="cover" /> : <View style={[styles.offlineVideoThumb, { backgroundColor: '#1a1a1a' }]} />}
                      {/* Members-only videos (3.5a): a lock chip when this
                          viewer can't watch it yet (the server drops cards
                          nobody could join for), a plain chip when they can. */}
                      {v.membersOnly === true ? (
                        <View style={[styles.offlineMembersChip, v.locked === true && styles.offlineMembersChipLocked]}>
                          <Ionicons
                            name={v.locked === true ? 'lock-closed' : 'star'}
                            size={9}
                            color={v.locked === true ? '#fff' : '#0b0b0b'}
                            style={{ marginRight: 3 }}
                          />
                          <Text style={[styles.offlineMembersChipText, v.locked === true && { color: '#fff' }]}>
                            {videoTierChip(v.membersMinTier) || 'Members'}
                          </Text>
                        </View>
                      ) : null}
                      <Text style={styles.offlineVideoTitle} numberOfLines={1}>{v.title || 'Video'}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              ) : null}
              <TouchableOpacity style={styles.browseBtn} onPress={goBackToStreams}>
                <Text style={styles.browseBtnText}>Browse live streams</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </View>

      {/* Info + engagement row (wraps the engage row onto its own line when
          narrow, 3.5a) */}
      <View style={[styles.infoRow, engageWrap && styles.infoRowWrap, { maxWidth: playerWidth }]}>
        {stream.avatarUrl ? (
          <Image source={{ uri: stream.avatarUrl }} style={styles.avatar} />
        ) : (
          <View style={[styles.avatar, styles.avatarFallback]}>
            <Text style={styles.avatarFallbackText}>
              {(stream.displayName || '??').slice(0, 2).toUpperCase()}
            </Text>
          </View>
        )}
        <View style={{ flex: 1, minWidth: 0, marginLeft: 10 }}>
          <Text style={styles.streamTitle} numberOfLines={2}>
            {stream.title || `${stream.displayName} is live`}
          </Text>
          {/* Streamer handle → public profile (founder 2026-07-10) */}
          <Text style={styles.streamerName}>
            <Text style={styles.streamerNameLink} onPress={() => openProfile(stream.displayName)}>
              {stream.displayName}
            </Text>
            {stream.followerCount > 0 ? `  ·  ${fmtCount(stream.followerCount)} follower${stream.followerCount === 1 ? '' : 's'}` : ''}
          </Text>
          <View style={styles.chipRow}>
            {stream.game ? (
              <View style={styles.gameChip}><Text style={styles.gameChipText}>{streamGameLabel(stream)}</Text></View>
            ) : null}
            {stream.live ? (
              <>
                <View style={styles.liveChip}><Text style={styles.liveChipText}>LIVE</Text></View>
                <Text style={styles.metaText}>
                  {stream.viewers} watching{stream.startedAtMs ? ` · ${fmtDuration(stream.startedAtMs, nowMs)}` : ''}
                </Text>
              </>
            ) : null}
          </View>
        </View>

        {!isSelf && (
          // Like sits LEFT of Subscribe (founder 2026-07-09); Rumble/Super
          // Crit moved into the chat panel above the message input. Join /
          // Member (3.5a) come last.
          <View style={[styles.engageRow, engageWrap && styles.engageRowNarrow]}>
            <TouchableOpacity
              style={styles.flagBtn}
              onPress={() => openReport('stream', { uid: userId, name: stream.displayName })}
              accessibilityLabel="Report this stream"
            >
              <Ionicons name="flag-outline" size={15} color="#777" />
            </TouchableOpacity>
            <TouchableOpacity style={styles.likeBtn} onPress={toggleLike} disabled={likeBusy}>
              <Ionicons
                name={stream.liked ? 'heart' : 'heart-outline'}
                size={16}
                color={stream.liked ? COLORS.LIVE : '#bbb'}
                style={{ marginRight: 5 }}
              />
              <Text style={styles.likeBtnText}>{fmtCount(stream.likeCount)}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.subscribeBtn, stream.following && styles.subscribedBtn]}
              onPress={toggleFollow}
              disabled={followBusy}
            >
              <Ionicons
                name={stream.following ? 'notifications' : 'notifications-outline'}
                size={14}
                color={stream.following ? COLORS.GOLD : '#0b0b0b'}
                style={{ marginRight: 6 }}
              />
              <Text style={[styles.subscribeBtnText, stream.following && styles.subscribedBtnText]}>
                {stream.following ? 'Following' : 'Follow'}
              </Text>
            </TouchableOpacity>
            {showMember ? (
              <TouchableOpacity
                style={styles.memberBtn}
                onPress={() => setMemberPanelOpen(true)}
                accessibilityRole="button"
                accessibilityLabel={stream.displayName ? `Member of ${stream.displayName}'s channel` : 'Member'}
              >
                <Ionicons name="star" size={14} color={COLORS.GOLD} style={{ marginRight: 6 }} />
                <Text style={styles.memberBtnText}>Member</Text>
              </TouchableOpacity>
            ) : showJoin ? (
              <TouchableOpacity style={styles.joinBtn} onPress={openJoin} accessibilityRole="button">
                <Ionicons name="star-outline" size={14} color={COLORS.GOLD} style={{ marginRight: 6 }} />
                <Text style={styles.joinBtnText}>Join</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        )}
      </View>

      {/* Team memberships (evergreen 3.5b): the channel owner's team, on its
          own full-width line under the engage row. ONE Pressable whose whole
          visible text is its label. */}
      {showTeamUpsell ? (
        <Pressable
          style={[styles.teamUpsellRow, { maxWidth: playerWidth }]}
          onPress={openTeamJoin}
          accessibilityRole="button"
          accessibilityLabel={`Also streams with ${teamNoun}. Join ${teamNoun}.`}
        >
          <Ionicons name="people-outline" size={15} color={COLORS.GOLD} style={{ marginRight: 8 }} />
          <Text style={styles.teamUpsellText}>{`Also streams with ${teamNoun}. Join ${teamNoun}.`}</Text>
        </Pressable>
      ) : showTeamMemberRow ? (
        <Pressable
          style={[styles.teamUpsellRowQuiet, { maxWidth: playerWidth }]}
          onPress={openTeamPage}
          accessibilityRole="link"
          accessibilityLabel={`Also streams with ${teamNoun}.`}
        >
          <Ionicons name="people-outline" size={15} color="#aaa" style={{ marginRight: 8 }} />
          <Text style={styles.teamUpsellTextQuiet}>{`Also streams with ${teamNoun}.`}</Text>
        </Pressable>
      ) : null}

      {/* Owner-only earn prompt (evergreen 3.2), in the spot the engage row
          leaves empty on your own channel. verifyToEarn is sent only to the
          streamer themselves and only while they fail the ID + age check that
          opens Super Crits (and, from 3.3, Rumbles); === true keeps it off
          for an older server. Kept out of StreamChat so the squad page and
          the OBS popout never show it. Same look as the CreatorMetrics
          "Verify to earn" banner. */}
      {isSelf && stream?.verifyToEarn === true ? (
        <TouchableOpacity
          style={[styles.verifyBanner, { maxWidth: playerWidth }]}
          onPress={() => navigation.navigate('IdVerification')}
          accessibilityRole="button"
        >
          <Ionicons name="id-card-outline" size={15} color={COLORS.GOLD} style={{ marginRight: 8 }} />
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={styles.verifyBannerStrong}>Verify to start earning</Text>
            <Text style={styles.verifyBannerText}>
              {membershipsFlagLive
                ? 'Super Crits and Rumbles open on your channel once your ID and age check are done. Then you can offer channel memberships.'
                : 'Super Crits and Rumbles open on your channel once your ID and age check are done.'}
            </Text>
          </View>
          <View style={styles.verifyBannerBtn}>
            <Text style={styles.verifyBannerBtnText}>Verify</Text>
          </View>
        </TouchableOpacity>
      ) : null}

      {/* YouTube-style description box: live stats + expandable details + game */}
      <DescriptionBox stream={stream} nowMs={nowMs} style={{ maxWidth: playerWidth }} />

      <TouchableOpacity style={styles.backButton} onPress={goBackToStreams}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Ionicons name="arrow-back" size={18} color="#888" style={{ marginRight: 4 }} />
          <Text style={styles.backButtonText}>Back to Streams</Text>
        </View>
      </TouchableOpacity>
    </>
  );

  // Loading shell (10-15): while getStreamPlayback answers, draw the page
  // instead of a lone spinner: the player box with the card's picture (or a
  // plain dark box) and the starting chip, plus placeholder channel lines.
  const renderShellColumn = () => (
    <>
      <View style={[styles.playerBox, { width: playerWidth }]}>
        {cardHandoff?.thumbnailUrl ? (
          <Image source={{ uri: cardHandoff.thumbnailUrl }} style={styles.shellPoster} resizeMode="cover" />
        ) : null}
        <StartingIndicator label={cardHandoff?.live ? STARTING_LABEL : null} />
      </View>
      <View style={[styles.infoRow, { maxWidth: playerWidth }]}>
        <View style={[styles.avatar, styles.shellBlock]} />
        <View style={{ flex: 1, minWidth: 0, marginLeft: 10, paddingTop: 4 }}>
          <View style={[styles.shellLine, { width: '60%' }]} />
          <View style={[styles.shellLine, { width: '35%', marginTop: 10 }]} />
        </View>
      </View>
    </>
  );

  // Same wrappers as the loaded layouts below, so the wide live-channels
  // rail mounts once and carries over (no second listLiveStreams call).
  const renderLoadingShell = () => (isWide ? (
    <View style={styles.wideBody}>
      <LiveRail
        collapsed={railCollapsed}
        onToggle={toggleRail}
        currentUserId={userId}
        navigation={navigation}
      />
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={[styles.content, styles.contentCentered]}
        showsVerticalScrollIndicator={false}
      >
        <View style={{ width: playerWidth, maxWidth: '100%' }}>
          {renderShellColumn()}
        </View>
      </ScrollView>
      <View style={chatCollapsed ? styles.chatExpandHandle : [styles.chatPanel, styles.chatRail]} />
    </View>
  ) : (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
      {renderShellColumn()}
    </ScrollView>
  ));

  // Wide mode hard-locks the page to the viewport (Twitch/YouTube behavior):
  // the chat rail and its input stay pinned; only the video column scrolls.
  const lockToViewport = isWide && Platform.OS === 'web';

  return (
    <View style={[styles.container, lockToViewport && styles.containerLocked]}>
      <Header navigation={navigation} />

      {/* Rumble (evergreen 3.3): meter, picker modal and burst watcher in ONE
          screen-level mount at a fixed spot in the tree, outside the wide /
          stacked branches and outside StreamChat, so collapsing chat, crossing
          the breakpoint or a failed poll (stream -> null) never unmounts an
          open payment form. Keyed by channel: a switch starts it fresh. The
          wrapper only gives the meter card the page gutter (the modal and
          burst portal out of it). */}
      {rumbleTarget ? (
        <View style={styles.rumbleMount}>
          <StreamRumble
            key={`stream:${userId}`}
            target={rumbleTarget}
            sessionId={chatSessionId}
            rumblesEnabled={canRumble}
            open={rumbleOpenFor === userId}
            onClose={closeRumble}
            onRequireAuth={rumbleRequireAuth}
          />
        </View>
      ) : null}

      {/* Join (evergreen 3.5a): screen level for the same reason as the
          Rumble picker, and keyed by kind and target (3.5b: the channel, or
          the owner's team from the upsell row), so a switch tears the payment
          form down. A failed poll never changes the target. */}
      {joinMountTarget ? (
        <JoinMembershipModal
          key={'join:' + joinMountTarget.kind + ':' + joinMountTarget.id}
          target={joinMountTarget}
          visible={!!joinTarget}
          openTo={joinOpenTo}
          minTierKey={joinMinTier}
          gatePerk={joinGatePerk}
          onClose={closeJoin}
          navigation={navigation}
        />
      ) : null}

      {loading ? (
        // No channel param never loads (fetchStream returns early), so it
        // keeps the plain spinner rather than a "Starting stream" that never ends.
        channelParam ? renderLoadingShell() : (
          <ActivityIndicator color={COLORS.GOLD} style={{ marginVertical: 60 }} />
        )
      ) : !stream ? (
        <View style={styles.offlineBox}>
          <Ionicons name="cloud-offline-outline" size={34} color={COLORS.DIM} />
          <Text style={styles.offlineTitle}>Streamer not found</Text>
        </View>
      ) : isWide ? (
        // Twitch layout: collapsible live-channels rail | centered content |
        // collapsible chat rail
        <View style={styles.wideBody}>
          <LiveRail
            collapsed={railCollapsed}
            onToggle={toggleRail}
            currentUserId={userId}
            navigation={navigation}
          />
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={[styles.content, styles.contentCentered]}
            showsVerticalScrollIndicator={false}
          >
            <View style={{ width: playerWidth, maxWidth: '100%' }}>
              {renderMainColumn()}
            </View>
          </ScrollView>
          {chatCollapsed ? (
            <TouchableOpacity
              style={styles.chatExpandHandle}
              onPress={toggleChat}
              accessibilityLabel="Expand chat"
            >
              <Ionicons name="chevron-back" size={16} color="#888" />
              <Ionicons name="chatbubble-ellipses-outline" size={16} color="#888" style={{ marginTop: 10 }} />
            </TouchableOpacity>
          ) : (
            <StreamChat
              sessionId={chatSessionId}
              senderName={myName}
              streamerUid={userId}
              navigation={navigation}
              style={styles.chatRail}
              onCollapse={toggleChat}
              onPopout={Platform.OS === 'web' && isSelf ? openChatPopout : undefined}
              onRumble={openRumble}
              onSuperCrit={openSuperCrit}
              rumblesEnabled={canRumble}
              hideSuperCrit={stream?.tipsEnabled === false}
              onUserPress={openChatUserCard}
              hiddenUids={hiddenUids}
              {...membershipChatProps}
            />
          )}
        </View>
      ) : (
        // Stacked layout: chat gets a fixed-height panel under the details
        <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
          {renderMainColumn()}
          <StreamChat
            sessionId={chatSessionId}
            senderName={myName}
            streamerUid={userId}
            navigation={navigation}
            style={styles.chatStacked}
            onPopout={Platform.OS === 'web' && isSelf ? openChatPopout : undefined}
            onRumble={openRumble}
            onSuperCrit={openSuperCrit}
            rumblesEnabled={canRumble}
            hideSuperCrit={stream?.tipsEnabled === false}
            onUserPress={openChatUserCard}
            hiddenUids={hiddenUids}
            {...membershipChatProps}
          />
        </ScrollView>
      )}

      {/* Super Crit modal — real-money highlighted chat message */}
      <Modal visible={showSuper} transparent animationType="fade" onRequestClose={closeSuperCrit}>
        <Pressable style={styles.rumbleBackdrop} onPress={closeSuperCrit}>
          <Pressable style={[styles.rumbleCard, superEmbedded && styles.superCardWide]} onPress={(e) => e.stopPropagation()}>
            {superSent ? (
              <View style={{ alignItems: 'center', paddingVertical: 18 }}>
                <Ionicons name="star" size={34} color={COLORS.GOLD} />
                <Text style={styles.rumbleSentText}>Super Crit sent! 💥</Text>
              </View>
            ) : superTips.confirm ? (
              <>
                {/* 'limits' mode: a held checkout payment being confirmed (L9) */}
                <Text style={styles.rumbleTitle}>Complete your Super Crit</Text>
                {superTips.confirm === 'confirming' ? (
                  <ActivityIndicator color={COLORS.GOLD} style={{ marginTop: 18 }} />
                ) : null}
                <Text style={styles.rumbleWaveHint}>
                  {superTips.confirm === 'confirming' ? CONFIRMING_TEXT : STILL_CONFIRMING_TEXT}
                </Text>
                {superTips.confirm === 'pending' ? (
                  <TouchableOpacity style={styles.superCancelBtn} onPress={closeSuperCrit}>
                    <Text style={styles.superCancelBtnText}>Close</Text>
                  </TouchableOpacity>
                ) : null}
              </>
            ) : superEmbedded ? (
              <>
                <Text style={styles.rumbleTitle}>Complete your Super Crit</Text>
                {superTips.cardRefusal ? (
                  <SpendingLimitNotice
                    line={cardLine(superTips.cardRefusal, { verify: superLimit?.verify })}
                    tone="error"
                    onVerify={verifyFromSuper}
                  />
                ) : null}
                <View ref={superCheckoutContainerRef} style={styles.superCheckoutBox} />
                <TouchableOpacity style={styles.superCancelBtn} onPress={closeSuperCrit}>
                  <Text style={styles.superCancelBtnText}>Cancel</Text>
                </TouchableOpacity>
              </>
            ) : (
              <>
                <Text style={styles.rumbleTitle}>Super Crit {stream?.displayName || 'the streamer'}</Text>
                <Text style={styles.rumbleSub}>
                  Pin your hype to the chat with a highlighted message — bigger Super Crits get bolder colors.
                </Text>

                {/* 'limits' mode: the room above the tiers (L1, or L3-L4 at
                    the limit); a refusal's own numbers replace it. */}
                {superLimit && !superTips.limitErr ? (
                  <SpendingLimitNotice limit={superLimit} onExpire={refreshSuperSetup} onVerify={verifyFromSuper} />
                ) : null}
                {superTips.limitErr ? (
                  <SpendingLimitNotice
                    limit={superTips.limitErr.limit}
                    amountCents={superTips.limitErr.amountCents}
                    onExpire={refreshSuperSetup}
                    onVerify={verifyFromSuper}
                  />
                ) : null}

                <View style={styles.superTierGrid}>
                  {(superSetup?.tiers || [
                    { id: 'sc_100', cents: 100, color: '#1565C0' },
                    { id: 'sc_300', cents: 300, color: '#00B8D4' },
                    { id: 'sc_500', cents: 500, color: '#00BFA5' },
                    { id: 'sc_1000', cents: 1000, color: '#FFB300' },
                    { id: 'sc_2500', cents: 2500, color: '#F57C00' },
                    { id: 'sc_5000', cents: 5000, color: '#E91E63' },
                    { id: 'sc_10000', cents: 10000, color: '#E62117' },
                    { id: 'sc_50000', cents: 50000, color: '#D00000' },
                  ]).map((t) => (
                    <TouchableOpacity
                      key={t.id}
                      style={[
                        styles.superTierChip,
                        { borderColor: t.color },
                        superTier === t.id && { backgroundColor: t.color },
                        // 'limits' mode: a tier above the room is disabled.
                        !fitsLimit(superLimit, t.cents) && { opacity: 0.35 },
                      ]}
                      onPress={() => { setSuperTier(t.id); superTips.clearNotices(); }}
                      disabled={!fitsLimit(superLimit, t.cents)}
                    >
                      <Text style={[
                        styles.superTierChipText,
                        superTier === t.id && { color: superCritTextColor(t.color) },
                      ]}>
                        {fmtDollars(t.cents)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>

                <TextInput
                  style={styles.superMsgInput}
                  placeholder="Say something (optional)…"
                  placeholderTextColor="#666"
                  value={superMsg}
                  onChangeText={setSuperMsg}
                  maxLength={superSetup?.maxMessageLength || 200}
                  multiline
                />

                {superSetup?.hasSavedCard && superSetup?.card && superSavedStatus === 'ok' ? (
                  <Text style={styles.rumbleWaveHint}>
                    One tap on your saved {superSetup.card.brand?.toUpperCase()} ····{superSetup.card.last4}
                  </Text>
                ) : null}
                {/* 'limits' mode: the saved card can't be used here (L5s /
                    L6); "Use another card" and Send both open checkout for
                    another card (sendSuperCrit skips one-tap then). */}
                {superSetup?.hasSavedCard && superSavedStatus !== 'ok' ? (
                  <SpendingLimitNotice
                    line={cardLine(superSavedStatus, { saved: true, verify: superLimit?.verify })}
                    tone="error"
                    onVerify={verifyFromSuper}
                    onUseAnotherCard={superLimitBlocks ? undefined : sendSuperCrit}
                    disabled={superBusy}
                  />
                ) : null}
                {superTips.outcomeLine ? (
                  <SpendingLimitNotice line={superTips.outcomeLine} tone="error" onVerify={verifyFromSuper} />
                ) : null}

                {superError ? <Text style={styles.rumbleErrorText}>{superError}</Text> : null}

                <TouchableOpacity
                  style={[styles.rumbleSendBtn, (superBusy || superLimitBlocks) && { opacity: 0.6 }]}
                  onPress={sendSuperCrit}
                  disabled={superBusy || superLimitBlocks}
                >
                  {superBusy ? (
                    <ActivityIndicator size="small" color="#0b0b0b" />
                  ) : (
                    <Text style={styles.rumbleSendBtnText}>
                      Send {fmtDollars(selectedSuperCents)} Super Crit
                    </Text>
                  )}
                </TouchableOpacity>
              </>
            )}
          </Pressable>
        </Pressable>
      </Modal>

      {/* Member panel (3.5a): what this member has, from their own pair doc.
          Money-free; billing lives on Your memberships. */}
      <Modal
        visible={memberPanelOpen && showMember}
        transparent
        animationType="fade"
        onRequestClose={() => setMemberPanelOpen(false)}
      >
        <Pressable style={styles.rumbleBackdrop} onPress={() => setMemberPanelOpen(false)}>
          <Pressable style={styles.rumbleCard} onPress={(e) => e.stopPropagation()}>
            <View style={styles.memberPanelHeader}>
              <Ionicons name="star" size={18} color={COLORS.GOLD} style={{ marginRight: 8 }} />
              <Text style={[styles.rumbleTitle, { flex: 1 }]}>
                {joinNameRef.current ? `Member of ${joinNameRef.current}'s channel` : 'Member'}
              </Text>
            </View>
            {fmtMonthYear(memberDoc?.memberSinceMs) ? (
              <Text style={styles.rumbleSub}>Member since {fmtMonthYear(memberDoc?.memberSinceMs)}</Text>
            ) : null}
            {/* Tiers (R5): the tier whose perks the member has now. */}
            {isTierKey(memberDoc?.tierKey) ? (
              <Text style={styles.rumbleSub}>{yourTierLine(tierNameOf(memberDoc.tierKey))}</Text>
            ) : null}
            {memberPerkKeys.length ? (
              <View style={styles.memberPerkList}>
                {memberPerkKeys.map((k) => (
                  <View key={k} style={styles.memberPerkRow}>
                    <Ionicons name="checkmark" size={14} color={COLORS.GOLD} style={{ marginRight: 8 }} />
                    <Text style={styles.memberPerkText}>{PERK_LABELS[k]}</Text>
                  </View>
                ))}
              </View>
            ) : null}
            {memberDoc?.removedByCreator === true && formatMembershipDate(memberDoc?.endsAtMs) ? (
              <Text style={styles.rumbleSub}>Your membership ends on {formatMembershipDate(memberDoc.endsAtMs)}.</Text>
            ) : null}
            <TouchableOpacity style={styles.rumbleSendBtn} onPress={openManageMemberships} accessibilityRole="button">
              <Text style={styles.rumbleSendBtnText}>Manage membership</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Chat user card — tap a handle in chat: profile / hide / mute / report */}
      <Modal visible={!!chatUserCard} transparent animationType="fade" onRequestClose={() => setChatUserCard(null)}>
        <Pressable style={styles.rumbleBackdrop} onPress={() => setChatUserCard(null)}>
          <Pressable style={styles.userCard} onPress={(e) => e.stopPropagation()}>
            {chatUserCard ? (
              <>
                <View style={styles.userCardHeader}>
                  <Text style={styles.userCardName}>{chatUserCard.user}</Text>
                  {chatUserCard.userId === userId ? (
                    <View style={styles.streamerBadge}>
                      <Ionicons name="videocam" size={9} color="#0b0b0b" />
                    </View>
                  ) : null}
                </View>
                {chatUserCard.userId !== userId && chatUserCard.memberMonths != null ? (
                  <View style={styles.userCardMemberRow}>
                    <Ionicons name="star" size={13} color={COLORS.GOLD} style={{ marginRight: 8 }} />
                    {/* Tiers (R5): the member's tier chip beside the tenure. */}
                    {chatUserCard.memberTierKey ? (
                      <View
                        style={[styles.memberChip, {
                          backgroundColor: badgeColors(chatUserCard.memberTierKey).fill,
                          borderColor: badgeColors(chatUserCard.memberTierKey).border,
                        }]}
                        accessibilityLabel={badgeA11y(chatUserCard.memberTierKey, chatUserCard.memberMonths)}
                      >
                        <Text style={[styles.memberChipText, { color: badgeColors(chatUserCard.memberTierKey).text }]}>
                          {badgeLabel(chatUserCard.memberTierKey)}
                        </Text>
                      </View>
                    ) : null}
                    <Text style={styles.userCardMemberText}>{memberTenureLabel(chatUserCard.memberMonths)}</Text>
                  </View>
                ) : null}

                <TouchableOpacity style={styles.userCardAction} onPress={() => { setChatUserCard(null); openProfile(chatUserCard.user); }}>
                  <Ionicons name="person-outline" size={16} color="#ccc" style={{ marginRight: 10 }} />
                  <Text style={styles.userCardActionText}>View profile</Text>
                </TouchableOpacity>

                {chatUserCard.userId !== auth.currentUser?.uid ? (
                  <>
                    <TouchableOpacity style={styles.userCardAction} onPress={() => toggleHideUser(chatUserCard.userId)}>
                      <Ionicons name={hiddenUids.has(chatUserCard.userId) ? 'eye-outline' : 'eye-off-outline'} size={16} color="#ccc" style={{ marginRight: 10 }} />
                      <Text style={styles.userCardActionText}>
                        {hiddenUids.has(chatUserCard.userId) ? 'Show their messages' : 'Hide their messages (just for you)'}
                      </Text>
                    </TouchableOpacity>

                    {auth.currentUser?.uid === userId && chatUserCard.userId !== userId ? (
                      <TouchableOpacity
                        style={styles.userCardAction}
                        onPress={() => toggleMuteUser(chatUserCard.userId)}
                        disabled={modBusy}
                      >
                        <Ionicons name="mic-off-outline" size={16} color={COLORS.GOLD} style={{ marginRight: 10 }} />
                        <Text style={[styles.userCardActionText, { color: COLORS.GOLD }]}>
                          {modBusy ? 'Working…' : mutedUids.has(chatUserCard.userId) ? 'Unmute in this chat' : 'Mute in this chat'}
                        </Text>
                      </TouchableOpacity>
                    ) : null}

                    <TouchableOpacity
                      style={styles.userCardAction}
                      onPress={() => openReport('chat_user', {
                        uid: chatUserCard.userId, name: chatUserCard.user,
                        messageId: chatUserCard.messageId, messageText: chatUserCard.messageText,
                      })}
                    >
                      <Ionicons name="flag-outline" size={16} color="#e05555" style={{ marginRight: 10 }} />
                      <Text style={[styles.userCardActionText, { color: '#e05555' }]}>Report</Text>
                    </TouchableOpacity>
                  </>
                ) : null}
              </>
            ) : null}
          </Pressable>
        </Pressable>
      </Modal>

      {/* Report modal — streams and chat users; reports go straight to admins */}
      <Modal visible={!!reportTarget} transparent animationType="fade" onRequestClose={() => setReportTarget(null)}>
        <Pressable style={styles.rumbleBackdrop} onPress={() => setReportTarget(null)}>
          <Pressable style={styles.rumbleCard} onPress={(e) => e.stopPropagation()}>
            {reportStatus === 'sent' ? (
              <View style={{ alignItems: 'center', paddingVertical: 18 }}>
                <Ionicons name="shield-checkmark" size={34} color={COLORS.GOLD} />
                <Text style={styles.rumbleSentText}>Report sent — our team will review.</Text>
              </View>
            ) : reportTarget ? (
              <>
                <Text style={styles.rumbleTitle}>
                  Report {reportTarget.kind === 'stream' ? 'this stream' : reportTarget.name}
                </Text>
                <Text style={styles.rumbleSub}>
                  Tell us what's wrong — reports go straight to the CritCash team.
                </Text>

                <View style={styles.reportChips}>
                  {[
                    ['inappropriate', 'Inappropriate'],
                    ['harassment', 'Harassment'],
                    ['cheating', 'Cheating'],
                    ['spam', 'Spam'],
                    ['other', 'Other'],
                  ].map(([key, label]) => (
                    <TouchableOpacity
                      key={key}
                      style={[styles.reportChip, reportReason === key && styles.reportChipActive]}
                      onPress={() => setReportReason(key)}
                    >
                      <Text style={[styles.reportChipText, reportReason === key && styles.reportChipTextActive]}>
                        {label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>

                <TextInput
                  style={styles.superMsgInput}
                  placeholder="Anything else we should know? (optional)"
                  placeholderTextColor="#666"
                  value={reportDetails}
                  onChangeText={setReportDetails}
                  maxLength={500}
                  multiline
                />

                {reportStatus ? <Text style={styles.rumbleErrorText}>{reportStatus}</Text> : null}

                <TouchableOpacity
                  style={[styles.rumbleSendBtn, reportBusy && { opacity: 0.6 }]}
                  onPress={submitReport}
                  disabled={reportBusy}
                >
                  {reportBusy ? (
                    <ActivityIndicator size="small" color="#0b0b0b" />
                  ) : (
                    <Text style={styles.rumbleSendBtnText}>Send report</Text>
                  )}
                </TouchableOpacity>
              </>
            ) : null}
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.BG },
  // Viewport lock (wide/web): the page itself never scrolls — the center
  // column gets its own scrollbar, chat + rails stay pinned.
  containerLocked: { height: '100vh', maxHeight: '100vh', overflow: 'hidden' },
  // Screen-level StreamRumble (3.3): page gutter for its meter card
  rumbleMount: { paddingHorizontal: 16 },
  content: { padding: 16, paddingBottom: 100 },
  contentCentered: { alignItems: 'center' },
  wideBody: { flex: 1, flexDirection: 'row', minHeight: 0 },

  // Live channels rail (left, collapsible — Twitch pattern)
  rail: {
    borderRightWidth: 1,
    borderRightColor: COLORS.CARD_BORDER,
    backgroundColor: '#0e0e0e',
  },
  railHeader: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 12, paddingVertical: 12,
  },
  railTitle: { color: '#888', fontSize: 11, fontWeight: '700', letterSpacing: 0.8 },
  railRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 10, paddingVertical: 6,
  },
  railRowCollapsed: { justifyContent: 'center', paddingHorizontal: 0 },
  railRowActive: { backgroundColor: '#1a1a1a' },
  railAvatar: { width: 30, height: 30, borderRadius: 15 },
  railAvatarFallback: { backgroundColor: '#2a3550', alignItems: 'center', justifyContent: 'center' },
  railAvatarText: { color: '#b5d4f4', fontSize: 10, fontWeight: '700' },
  railName: { color: '#eee', fontSize: 12, fontWeight: '600' },
  railGame: { color: '#777', fontSize: 11 },
  railViewerCell: { flexDirection: 'row', alignItems: 'center', marginLeft: 6 },
  railLiveDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: COLORS.LIVE, marginRight: 4 },
  railViewers: { color: '#bbb', fontSize: 11 },

  // Collapsed-chat expand handle (slim right edge strip)
  chatExpandHandle: {
    width: CHAT_COLLAPSED_WIDTH,
    borderLeftWidth: 1,
    borderLeftColor: COLORS.CARD_BORDER,
    backgroundColor: '#0e0e0e',
    alignItems: 'center',
    paddingTop: 14,
  },

  matchBanner: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#241d00',
    borderColor: COLORS.GOLD, borderWidth: 1, borderRadius: 8,
    paddingVertical: 10, paddingHorizontal: 12, marginBottom: 12,
  },
  matchBannerText: { flex: 1, color: COLORS.GOLD, fontSize: 13, fontWeight: '700' },

  playerBox: {
    aspectRatio: 16 / 9, borderRadius: 10, overflow: 'hidden', backgroundColor: '#000',
  },
  offlineVideo: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#141414' },
  offlineThumb: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', opacity: 0.35 },
  offlineOverlay: { alignItems: 'center' },
  offlineBox: { alignItems: 'center', paddingVertical: 60 },
  offlineTitle: { color: COLORS.TEXT, fontSize: 16, fontWeight: '700', marginTop: 10 },
  browseBtn: {
    backgroundColor: COLORS.GOLD, borderRadius: 8, paddingVertical: 10,
    paddingHorizontal: 18, marginTop: 14,
  },
  browseBtnText: { color: '#0b0b0b', fontSize: 13, fontWeight: '800' },
  offlineNext: { color: '#FCD000', fontSize: 13, fontWeight: '700', marginTop: 6 },
  offlineLast: { color: '#888', fontSize: 12, marginTop: 6 },
  offlineShelf: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 8, marginTop: 12, maxWidth: 720 },
  offlineVideoCard: { width: 160 },
  offlineVideoThumb: { width: 160, height: 90, borderRadius: 6, backgroundColor: '#222' },
  offlineVideoTitle: { color: '#ccc', fontSize: 11, marginTop: 4 },
  // Members-only video chips on the offline shelf (3.5a)
  offlineMembersChip: {
    position: 'absolute', top: 5, left: 5,
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: COLORS.GOLD, borderRadius: 4, paddingVertical: 2, paddingHorizontal: 5,
  },
  offlineMembersChipLocked: { backgroundColor: 'rgba(0,0,0,0.75)', borderWidth: 1, borderColor: COLORS.GOLD },
  offlineMembersChipText: { color: '#0b0b0b', fontSize: 9, fontWeight: '800', letterSpacing: 0.3 },

  infoRow: { flexDirection: 'row', marginTop: 14 },
  // Narrow (3.5a): the engage row wraps under the avatar and title
  infoRowWrap: { flexWrap: 'wrap' },
  avatar: { width: 44, height: 44, borderRadius: 22 },
  avatarFallback: { backgroundColor: '#2a3550', alignItems: 'center', justifyContent: 'center' },
  avatarFallbackText: { color: '#b5d4f4', fontSize: 14, fontWeight: '700' },
  streamTitle: { color: '#fff', fontSize: 16, fontWeight: '700' },
  streamerName: { color: COLORS.DIM, fontSize: 13, marginTop: 2 },
  streamerNameLink: { color: '#ccc', fontWeight: '700' },
  chipRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' },
  gameChip: { backgroundColor: '#1c1c1c', borderRadius: 4, paddingVertical: 2, paddingHorizontal: 8 },
  gameChipText: { color: '#999', fontSize: 11 },
  liveChip: { backgroundColor: COLORS.LIVE, borderRadius: 4, paddingVertical: 2, paddingHorizontal: 7 },
  liveChipText: { color: '#fff', fontSize: 10, fontWeight: '800', letterSpacing: 0.5 },
  metaText: { color: COLORS.DIM, fontSize: 12 },

  // Like left of Subscribe (founder 2026-07-09)
  engageRow: { flexDirection: 'row', alignItems: 'center', marginLeft: 10, gap: 8 },
  engageRowNarrow: { flexBasis: '100%', marginLeft: 0, marginTop: 10, flexWrap: 'wrap' },
  // Channel memberships (3.5a). Never the subscribeBtn* styles, which mean
  // Follow on this page.
  joinBtn: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#241d00',
    borderWidth: 1, borderColor: COLORS.GOLD,
    borderRadius: 8, paddingVertical: 7, paddingHorizontal: 14,
  },
  joinBtnText: { color: COLORS.GOLD, fontSize: 13, fontWeight: '800' },
  memberBtn: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#1c1c1c',
    borderWidth: 1, borderColor: '#4a3d00',
    borderRadius: 8, paddingVertical: 7, paddingHorizontal: 14,
  },
  memberBtnText: { color: COLORS.GOLD, fontSize: 13, fontWeight: '800' },
  // Team memberships upsell (3.5b): the joinBtn colours on a full-width line
  // under the engage row; the member's quieter twin links to the team page.
  teamUpsellRow: {
    flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch', minHeight: 36,
    backgroundColor: '#241d00', borderWidth: 1, borderColor: COLORS.GOLD,
    borderRadius: 8, paddingVertical: 8, paddingHorizontal: 14, marginTop: 10,
  },
  teamUpsellText: { flex: 1, color: COLORS.GOLD, fontSize: 13, fontWeight: '800' },
  teamUpsellRowQuiet: {
    flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch', minHeight: 36,
    backgroundColor: '#1c1c1c', borderWidth: 1, borderColor: '#2a2a2a',
    borderRadius: 8, paddingVertical: 8, paddingHorizontal: 14, marginTop: 10,
  },
  teamUpsellTextQuiet: { flex: 1, color: '#bbb', fontSize: 13, fontWeight: '600' },
  memberPanelHeader: { flexDirection: 'row', alignItems: 'center' },
  memberPerkList: { marginTop: 12 },
  memberPerkRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 4 },
  memberPerkText: { color: '#ddd', fontSize: 13, flexShrink: 1 },
  subscribeBtn: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: COLORS.GOLD,
    borderRadius: 8, paddingVertical: 8, paddingHorizontal: 14,
  },
  subscribedBtn: { backgroundColor: '#241d00', borderWidth: 1, borderColor: COLORS.GOLD },
  subscribeBtnText: { color: '#0b0b0b', fontSize: 13, fontWeight: '800' },
  subscribedBtnText: { color: COLORS.GOLD },
  likeBtn: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#1c1c1c',
    borderRadius: 8, paddingVertical: 8, paddingHorizontal: 12,
  },
  likeBtnText: { color: '#bbb', fontSize: 12, fontWeight: '700' },
  flagBtn: {
    alignItems: 'center', justifyContent: 'center', backgroundColor: '#1c1c1c',
    borderRadius: 8, paddingVertical: 8, paddingHorizontal: 10,
  },

  // Owner-only "Verify to start earning" prompt (mirrors CreatorMetrics'
  // memberBanner look)
  verifyBanner: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#1a1604',
    borderColor: '#4a3d00', borderWidth: 1, borderRadius: 8,
    paddingVertical: 10, paddingHorizontal: 12, marginTop: 12,
  },
  verifyBannerStrong: { color: COLORS.GOLD, fontSize: 12, fontWeight: '700', lineHeight: 17 },
  verifyBannerText: { color: '#ccc', fontSize: 12, lineHeight: 17 },
  verifyBannerBtn: {
    backgroundColor: COLORS.GOLD, borderRadius: 8,
    paddingVertical: 6, paddingHorizontal: 12, marginLeft: 10,
  },
  verifyBannerBtnText: { color: '#0b0b0b', fontSize: 12, fontWeight: '800' },

  // Chat user card (tap a handle in chat)
  userCard: {
    width: '100%', maxWidth: 320, backgroundColor: COLORS.CARD,
    borderColor: COLORS.CARD_BORDER, borderWidth: 1, borderRadius: 14, padding: 8,
  },
  userCardHeader: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingVertical: 10, paddingHorizontal: 12,
    borderBottomWidth: 1, borderBottomColor: COLORS.CARD_BORDER, marginBottom: 4,
  },
  userCardName: { color: '#fff', fontSize: 15, fontWeight: '800' },
  userCardAction: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: 11, paddingHorizontal: 12, borderRadius: 8,
  },
  userCardActionText: { color: '#ccc', fontSize: 13.5, fontWeight: '600' },
  userCardMemberRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingHorizontal: 12 },
  userCardMemberText: { color: COLORS.GOLD, fontSize: 12.5, fontWeight: '700' },

  // Report modal reason chips
  reportChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 16 },
  reportChip: {
    backgroundColor: '#161616', borderColor: '#2a2a2a', borderWidth: 1,
    borderRadius: 8, paddingVertical: 7, paddingHorizontal: 12,
  },
  reportChipActive: { backgroundColor: '#2a0d0d', borderColor: '#e05555' },
  reportChipText: { color: '#bbb', fontSize: 12, fontWeight: '700' },
  reportChipTextActive: { color: '#ff8a8a' },

  // Custom live-player controls (no seek bar, no time — pause and live only)
  centerPlayBtn: {
    position: 'absolute', top: '50%', left: '50%',
    marginTop: -28, marginLeft: -28,
    width: 56, height: 56, borderRadius: 28,
    backgroundColor: 'rgba(0,0,0,0.65)',
    alignItems: 'center', justifyContent: 'center',
    paddingLeft: 4,
  },
  playerControlsBar: {
    position: 'absolute', left: 0, right: 0, bottom: 0,
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 10, paddingVertical: 8,
    backgroundColor: 'rgba(0,0,0,0.55)',
  },
  playerCtrlBtn: {
    width: 30, height: 30, borderRadius: 6,
    alignItems: 'center', justifyContent: 'center',
  },
  playerLiveChip: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.12)', borderRadius: 4,
    paddingVertical: 3, paddingHorizontal: 8, marginLeft: 4,
  },
  playerLiveDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: COLORS.LIVE, marginRight: 5 },
  playerLiveText: { color: '#fff', fontSize: 10, fontWeight: '800', letterSpacing: 0.6 },
  // "Starting stream" chip (10-15), centered over the poster
  startingWrap: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    alignItems: 'center', justifyContent: 'center',
  },
  startingChip: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: 'rgba(0,0,0,0.65)', borderRadius: 18,
    paddingVertical: 8, paddingHorizontal: 14,
  },
  startingChipText: { color: '#fff', fontSize: 13, fontWeight: '700' },
  // Loading shell (10-15): poster fill + placeholder channel lines
  shellPoster: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' },
  shellBlock: { backgroundColor: '#1a1a1a' },
  shellLine: { height: 12, borderRadius: 4, backgroundColor: '#1a1a1a' },

  // Modal card idiom (Super Crit / report; named for the old Rumble modal)
  rumbleBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.8)',
    justifyContent: 'center', alignItems: 'center', padding: 16,
  },
  rumbleCard: {
    width: '100%', maxWidth: 380, backgroundColor: COLORS.CARD,
    borderColor: COLORS.CARD_BORDER, borderWidth: 1, borderRadius: 14, padding: 20,
  },
  rumbleTitle: { color: '#fff', fontSize: 17, fontWeight: '800' },
  rumbleSub: { color: '#999', fontSize: 12.5, lineHeight: 18, marginTop: 6 },
  rumbleWaveHint: { color: '#777', fontSize: 12, marginTop: 10, textAlign: 'center' },
  rumbleErrorText: { color: '#e05555', fontSize: 12, marginTop: 10, textAlign: 'center' },
  rumbleSendBtn: {
    backgroundColor: COLORS.GOLD, borderRadius: 10, paddingVertical: 12,
    alignItems: 'center', marginTop: 14,
  },
  rumbleSendBtnText: { color: '#0b0b0b', fontSize: 14, fontWeight: '800' },
  rumbleSentText: { color: COLORS.GOLD, fontSize: 16, fontWeight: '800', marginTop: 10 },

  chatRumble: {
    flexDirection: 'row', alignItems: 'flex-start', backgroundColor: '#241d00',
    borderRadius: 6, paddingVertical: 6, paddingHorizontal: 8, marginBottom: 7,
  },
  chatRumbleText: { color: '#ddd', fontSize: 12.5, flexShrink: 1 },
  chatRumbleUser: { color: COLORS.GOLD, fontWeight: '700' },

  // Super Crit highlighted chat message (tier-colored header)
  superCritCard: { borderRadius: 8, overflow: 'hidden', marginBottom: 7, backgroundColor: '#1a1a1a' },
  superCritHeader: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: 6, paddingHorizontal: 9,
  },
  superCritUser: { flex: 1, fontSize: 12.5, fontWeight: '800' },
  superCritAmount: { fontSize: 12.5, fontWeight: '800', marginLeft: 8 },
  superCritBody: { color: '#eee', fontSize: 12.5, paddingVertical: 6, paddingHorizontal: 9 },

  // Streamer identity in chat (cyan handle + host badge)
  streamerNameWrap: { flexDirection: 'row', alignItems: 'center', marginRight: 6 },
  streamerBadge: {
    backgroundColor: '#3DD6F5', borderRadius: 3, width: 14, height: 14,
    alignItems: 'center', justifyContent: 'center', marginRight: 4,
  },
  chatMsgUserStreamer: { color: '#3DD6F5', fontSize: 12.5, fontWeight: '800' },
  // Bridged-message platform badge ([Twitch]/[Kick]/[YouTube]) — border/text
  // take the platform color inline.
  extBadge: {
    borderWidth: 1, borderRadius: 3, paddingHorizontal: 3, paddingVertical: 0.5,
    marginRight: 5, alignSelf: 'center',
  },
  extBadgeText: { fontSize: 8.5, fontWeight: '800', letterSpacing: 0.4 },
  // Member badge in chat (3.5a): gold chip before the handle
  // (tiers, R5: the tier's fill and border are set inline, badgeColors)
  memberChip: {
    backgroundColor: COLORS.GOLD, borderColor: COLORS.GOLD, borderWidth: 1, borderRadius: 3,
    paddingHorizontal: 4, paddingVertical: 0,
    marginRight: 5, alignSelf: 'center',
  },
  memberChipText: { color: '#0b0b0b', fontSize: 8.5, fontWeight: '800', letterSpacing: 0.4 },
  chatMsgBlock: { marginBottom: 7 },
  chatErrorText: {
    color: '#e05555', fontSize: 11.5, textAlign: 'center',
    paddingVertical: 6, paddingHorizontal: 12,
  },
  pinMsgBtn: {
    flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start',
    backgroundColor: '#241d00', borderWidth: 1, borderColor: COLORS.GOLD,
    borderRadius: 6, paddingVertical: 4, paddingHorizontal: 10, marginTop: 4,
  },
  pinMsgBtnText: { color: COLORS.GOLD, fontSize: 11, fontWeight: '800' },

  // Pinned message banner (top of chat)
  pinnedBar: {
    flexDirection: 'row', alignItems: 'flex-start',
    backgroundColor: '#1c1810', borderBottomWidth: 1, borderBottomColor: '#3a3010',
    paddingVertical: 8, paddingHorizontal: 12,
  },
  pinnedText: { flex: 1, color: '#ddd', fontSize: 12, lineHeight: 16, marginRight: 6 },
  pinnedUser: { color: COLORS.GOLD, fontWeight: '700' },

  // Rumble / Super Crit action row (inside chat, above the input)
  chatActionRow: {
    flexDirection: 'row', gap: 8,
    paddingHorizontal: 10, paddingTop: 8,
    borderTopWidth: 1, borderTopColor: COLORS.CARD_BORDER,
  },
  chatActionRumble: {
    flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#241d00', borderWidth: 1, borderColor: COLORS.GOLD,
    borderRadius: 8, paddingVertical: 7,
  },
  chatActionRumbleText: { color: COLORS.GOLD, fontSize: 12, fontWeight: '800' },
  chatActionSuper: {
    flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: COLORS.GOLD, borderRadius: 8, paddingVertical: 7,
  },
  chatActionSuperText: { color: '#0b0b0b', fontSize: 12, fontWeight: '800' },

  // Super Crit modal
  superTierGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 16 },
  superTierChip: {
    width: '22%', flexGrow: 1, alignItems: 'center',
    backgroundColor: '#161616', borderWidth: 1.5, borderRadius: 8, paddingVertical: 10,
  },
  superTierChipText: { color: '#ddd', fontSize: 13, fontWeight: '800' },
  superMsgInput: {
    backgroundColor: '#161616', borderColor: '#2a2a2a', borderWidth: 1,
    borderRadius: 8, color: COLORS.TEXT, fontSize: 13,
    paddingVertical: 8, paddingHorizontal: 12, marginTop: 12,
    minHeight: 60, textAlignVertical: 'top',
  },
  superCardWide: { maxWidth: 460 },
  superCheckoutBox: { minHeight: 420, marginTop: 14, borderRadius: 10, overflow: 'hidden', backgroundColor: '#fff' },
  superCancelBtn: { alignItems: 'center', marginTop: 10, paddingVertical: 8 },
  superCancelBtnText: { color: '#888', fontSize: 13, fontWeight: '600' },

  // YouTube-style description box
  descBox: {
    backgroundColor: '#181818', borderRadius: 12, padding: 14, marginTop: 14,
  },
  descStats: { color: '#fff', fontSize: 13, fontWeight: '700', marginBottom: 8 },
  descText: { color: '#ccc', fontSize: 13, lineHeight: 20 },
  descLink: { color: '#6ea8ff' },
  descToggle: { color: '#fff', fontSize: 13, fontWeight: '700', marginTop: 8 },
  descGameRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 4,
  },
  descTextBlock: {
    borderTopWidth: 1, borderTopColor: '#242424', paddingTop: 12, marginTop: 12,
  },
  descGameArt: { width: 44, height: 58, borderRadius: 8, backgroundColor: '#242424' },
  descGameTile: {
    width: 44, height: 44, borderRadius: 8, backgroundColor: '#242424',
    alignItems: 'center', justifyContent: 'center',
  },
  descGameName: { color: '#eee', fontSize: 13, fontWeight: '700' },
  descGameSub: { color: '#888', fontSize: 11, marginTop: 1 },

  backButton: { marginTop: 22, alignSelf: 'flex-start' },
  backButtonText: { color: '#888', fontSize: 14 },

  // Chat
  chatPanel: {
    backgroundColor: COLORS.CARD, borderColor: COLORS.CARD_BORDER, borderWidth: 1,
    borderRadius: 10, overflow: 'hidden',
  },
  chatRail: { width: CHAT_WIDTH, borderRadius: 0, borderTopWidth: 0, borderRightWidth: 0, borderBottomWidth: 0 },
  chatStacked: { height: 380, marginTop: 16 },
  chatHeader: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: 10, paddingHorizontal: 14,
    borderBottomWidth: 1, borderBottomColor: COLORS.CARD_BORDER,
  },
  chatTitle: { color: '#fff', fontSize: 13, fontWeight: '800', letterSpacing: 0.4 },
  // With the owner's members-only chip (3.5a): one truncating line
  chatTitleWithChip: { flex: 1, minWidth: 0 },
  gateChip: {
    flexShrink: 0, marginLeft: 8, marginRight: 8,
    flexDirection: 'row', alignItems: 'center',
    borderWidth: 1, borderColor: '#333', borderRadius: 10,
    paddingVertical: 3, paddingHorizontal: 8,
  },
  gateChipOn: { backgroundColor: COLORS.GOLD, borderColor: COLORS.GOLD },
  gateChipPaused: { borderColor: COLORS.GOLD },
  gateChipText: { color: '#aaa', fontSize: 10.5, fontWeight: '800', letterSpacing: 0.3 },
  gateChipTextOn: { color: '#0b0b0b' },
  // Members-only room banner above the input (3.5a)
  membersOnlyBanner: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#1c1810', borderTopWidth: 1, borderTopColor: '#3a3010',
    paddingVertical: 8, paddingHorizontal: 12,
  },
  membersOnlyBannerText: { flex: 1, color: '#ddd', fontSize: 12, lineHeight: 16 },
  membersOnlyJoinBtn: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: COLORS.GOLD,
    borderRadius: 6, paddingVertical: 5, paddingHorizontal: 10, marginLeft: 8,
  },
  membersOnlyJoinText: { color: '#0b0b0b', fontSize: 12, fontWeight: '800' },
  chatMessages: { flex: 1, paddingHorizontal: 12 },
  chatNewMsgs: {
    position: 'absolute', bottom: 8, alignSelf: 'center',
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: COLORS.GOLD, borderRadius: 12,
    paddingHorizontal: 10, paddingVertical: 5,
  },
  chatNewMsgsText: { color: '#0b0b0b', fontSize: 11, fontWeight: '700' },
  chatEmpty: { color: '#666', fontSize: 12, textAlign: 'center', marginTop: 20 },
  chatMsg: { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 7 },
  chatMsgUser: { color: COLORS.GOLD, fontSize: 12.5, fontWeight: '700', marginRight: 6 },
  chatMsgText: { color: '#ddd', fontSize: 12.5, flexShrink: 1 },
  chatInputRow: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    padding: 10, borderTopWidth: 1, borderTopColor: COLORS.CARD_BORDER,
  },
  chatInput: {
    flex: 1, backgroundColor: '#161616', borderColor: '#2a2a2a', borderWidth: 1,
    borderRadius: 8, color: COLORS.TEXT, fontSize: 13,
    paddingVertical: 8, paddingHorizontal: 12,
  },
  chatSendBtn: {
    backgroundColor: COLORS.GOLD, borderRadius: 8, padding: 9,
    alignItems: 'center', justifyContent: 'center',
  },
  chatSignupCta: {
    padding: 12, borderTopWidth: 1, borderTopColor: COLORS.CARD_BORDER, alignItems: 'center',
  },
  chatSignupCtaText: { color: COLORS.GOLD, fontSize: 13, fontWeight: '800' },
  chatSignupCtaSub: { color: '#777', fontSize: 11, marginTop: 2, textAlign: 'center' },
});
